import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { Service, User, Business, Notification, BusinessBranding, RecurrenceMode, RecurringPreviewResponse } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import BookingStep1 from '../components/booking/BookingStep1';
import BookingStep4ServiceAddress, { type ServiceAddress } from '../components/booking/BookingStep4ServiceAddress';
import NavigationBar from '../components/NavigationBar';
import PermissionGate from '../components/PermissionGate';
import Footer from '../components/Footer';
import { Search, User as UserIcon, X, CheckCircle, AlertCircle, Users, Clock, AlertTriangle, Calendar, CalendarDays, Loader2, ChevronLeft, ChevronRight, ArrowLeft, UserPlus, Plus, HelpCircle, FileText, Heart, Lock, Repeat } from 'lucide-react';
import toast from 'react-hot-toast';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import { getThemeStyles } from '../utils/themeUtils';
import { getThemeColors as getLayoutThemeColors } from '../utils/themeSchemes';
import { convertHoursRangeToTimeZone, formatLocalYmd, getBrowserTimeZone, localDateAndTimeToUtcIso, resolveBusinessTimeZone } from '../utils/timezoneUtils';
import { searchCustomers as fuzzySearchCustomers } from '../utils/searchUtils';
import { canViewFullCustomerPhone } from '../utils/roleUtils';
import { displayCustomerPhone } from '../utils/phoneFormatUtils';
import {
  DEFAULT_BOOKING_SLOT_MINUTES,
  DURATION_LEAD_IN_TOOLTIP,
  availabilityCacheKey,
  buildHourDisplayTimeSlots,
  extendAvailabilityTimeSlots,
  isDurationLeadInSlot,
  isOwnerDisplaySlotAvailable,
  minutesToHhmm,
  parseClockTimeToMinutes,
  rawSlotStepMinutes,
  resolveBookingSlotMinutes,
} from '../utils/bookingSlotSettings';

interface BookingData {
  selectedServices: Service[];
  selectedProfessionals: { [serviceId: string]: User | undefined };
  selectedDate: Date | null;
  selectedTime: string;
  totalPrice: number;
  totalDuration: number;
  insistOnProfessional: { [serviceId: string]: boolean };
  serviceAddress?: ServiceAddress;
  rememberServiceAddress?: boolean;
}

/** Amber highlight for starts that cannot fit the selected duration (still selectable). */
const DURATION_LEAD_IN_SLOT_STYLE: React.CSSProperties = {
  color: '#92400E',
  backgroundColor: '#FFFBEB',
  border: '1px solid #FCD34D'
};

/** Grey + diagonal hatch for past date/time slots that remain selectable (business override). */
const PAST_SELECTABLE_SLOT_STYLE: React.CSSProperties = {
  color: '#9CA3AF',
  backgroundColor: '#F3F4F6',
  backgroundImage:
    'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(107, 114, 128, 0.2) 3px, rgba(107, 114, 128, 0.2) 6px)',
  border: '1px solid #E5E7EB',
};

const OwnerBookingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  
  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Check if date/time are pre-filled from calendar
  const prefilledDate = searchParams.get('date');
  const prefilledTime = searchParams.get('time');
  const hasPrefilledDateTime = !!(prefilledDate && prefilledTime);
  
  // Check if coming from "my appointments" page
  const fromMyAppointments = searchParams.get('from') === 'my-appointments';
  
  // Capture filter state from URL params to preserve when navigating back
  // Use different param names for my-appointments to avoid conflicts
  const preservedAppointmentsFilters = {
    dateRangeType: searchParams.get('dateRangeType') || '',
    dateRangeStart: searchParams.get('dateRangeStart') || '',
    status: searchParams.get('status') || '',
    professionalId: searchParams.get('professionalId') || '',
    view: searchParams.get('view') || ''
  };
  
  const preservedMyAppointmentsFilters = {
    myDateRangeType: searchParams.get('myDateRangeType') || '',
    myDateRangeStart: searchParams.get('myDateRangeStart') || '',
    myStatus: searchParams.get('myStatus') || '',
    myView: searchParams.get('myView') || ''
  };

  // Pre-fill professional when coming from business calendar slot click (professional column view)
  const prefillProfessionalId = searchParams.get('prefillProfessionalId') || '';
  
  // Notification state for sidebar badge
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  
  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };
  
  // Load unread notifications count for badge
  useEffect(() => {
    const loadUnreadCount = async () => {
      if (!currentBusiness?.id) return;
      try {
        // Make a lightweight call with limit 1 just to get the total count from pagination
        const response = await apiService.getNotifications(currentBusiness.id, { page: 1, limit: 1, isRead: false });
        // Use pagination.total for the actual count, not the array length
        const totalUnread = response.pagination?.total || 0;
        // Set a dummy array with the count for compatibility, but we'll use totalUnread for display
        setUnreadNotifications(Array(totalUnread).fill(null) as Notification[]);
      } catch (error) {
        console.error('Error loading unread notifications count:', error);
        setUnreadNotifications([]);
      }
    };
    loadUnreadCount();
  }, [currentBusiness?.id]);
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [professionals, setProfessionals] = useState<User[]>([]);
  const [priorityByProfessionalId, setPriorityByProfessionalId] = useState<Record<string, number>>({});
  const [business, setBusiness] = useState<Business | null>(null);
  const displayTimeZone = getBrowserTimeZone();
  const businessTimeZone = resolveBusinessTimeZone(business);
  const [branding, setBranding] = useState<BusinessBranding | null>(null);
  const [professionalCapabilities, setProfessionalCapabilities] = useState<{[professionalId: string]: any[]}>({});
  const [loadingCapabilities, setLoadingCapabilities] = useState(false);
  const [sameProfessionalForAll, setSameProfessionalForAll] = useState(true);
  const [operatingHours, setOperatingHours] = useState<any[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [professionalSchedules, setProfessionalSchedules] = useState<{[professionalId: string]: any[]}>({});
  const [professionalTimeOffs, setProfessionalTimeOffs] = useState<{[professionalId: string]: any[]}>({});
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [availabilityData, setAvailabilityData] = useState<{[date: string]: any}>({});
  const availabilityFetchSeqRef = useRef(0);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [businessClosures, setBusinessClosures] = useState<any[]>([]);
  const [loadingClosures, setLoadingClosures] = useState(false);
  const [bookingSlotMinutes, setBookingSlotMinutes] = useState(DEFAULT_BOOKING_SLOT_MINUTES);
  const slotStepMinutes = rawSlotStepMinutes(bookingSlotMinutes);

  const effectiveBusinessId = currentBusiness?.id || business?.id || '';

  useEffect(() => {
    if (!effectiveBusinessId) return;
    apiService
      .getAppointmentBookingSettings(effectiveBusinessId)
      .then((d) => setBookingSlotMinutes(resolveBookingSlotMinutes(d)))
      .catch(() => setBookingSlotMinutes(DEFAULT_BOOKING_SLOT_MINUTES));
  }, [effectiveBusinessId]);

  // Fetch display priorities (display-only ordering for professional selection UI)
  useEffect(() => {
    const fetchPriorities = async () => {
      if (!effectiveBusinessId) return;
      try {
        const rows = await apiService.getProfessionalDisplayPriorities(effectiveBusinessId);
        const map: Record<string, number> = {};
        rows.forEach((r) => {
          if (r && typeof r.professionalId === 'string' && typeof r.priority === 'number') {
            map[r.professionalId] = r.priority;
          }
        });
        setPriorityByProfessionalId(map);
      } catch (error) {
        // Fail open: preserve existing ordering if priorities can't be loaded
        console.error('Error fetching professional display priorities:', error);
        setPriorityByProfessionalId({});
      }
    };

    // Only relevant when there could be multiple professionals to display
    if (effectiveBusinessId && professionals.length >= 2) {
      fetchPriorities();
    } else {
      setPriorityByProfessionalId({});
    }
  }, [effectiveBusinessId, professionals.length]);

  const orderProfessionalsForDisplay = (list: User[]): User[] => {
    /**
     * IMPORTANT: Do NOT change eligibility/selection logic.
     * This function is display-only, stable, and preserves the existing order unless priorities exist.
     */
    if (list.length < 2) return list;
    if (Object.keys(priorityByProfessionalId).length === 0) return list;

    return list
      .map((p, idx) => ({
        p,
        idx,
        priority: priorityByProfessionalId[p.id]
      }))
      .sort((a, b) => {
        const aHas = typeof a.priority === 'number';
        const bHas = typeof b.priority === 'number';

        if (aHas && bHas && a.priority !== b.priority) return a.priority - b.priority;
        if (aHas !== bHas) return aHas ? -1 : 1;

        // Stable fallback: preserve existing order exactly
        return a.idx - b.idx;
      })
      .map(x => x.p);
  };
  
  // Date picker popup state (for mobile)
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [datePickerMonth, setDatePickerMonth] = useState(new Date());
  
  // Customer selection state
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<User | null>(null);
  const [customerSearchResults, setCustomerSearchResults] = useState<User[]>([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [emailLookupResults, setEmailLookupResults] = useState<User[]>([]);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [showEmailLookupDropdown, setShowEmailLookupDropdown] = useState(false);
  const [hasUnselectedEmailMatch, setHasUnselectedEmailMatch] = useState(false);
  const [matchedEmail, setMatchedEmail] = useState<string>('');
  const [manualCustomer, setManualCustomer] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const firstNameInputRef = useRef<HTMLInputElement>(null);
  
  // Private professional notes state
  const [privateNotes, setPrivateNotes] = useState<string>('');
  
  // Helper function to format email - hide auto-generated temp emails
  const formatCustomerEmail = (email: string | undefined | null): string => {
    if (!email) return 'No contact provided';
    if (email.startsWith('temp') && email.includes('@appointment.local')) {
      return 'No contact provided';
    }
    return email;
  };

  // Helper to normalize phone across different API shapes (phone vs phoneNumber)
  const getCustomerPhone = (c: any): string | undefined => {
    const phone = c?.phone ?? c?.phoneNumber ?? c?.phone_number;
    if (typeof phone !== 'string' || !phone.trim()) return undefined;
    return displayCustomerPhone(phone, canViewFullCustomerPhone(user, currentBusiness?.id));
  };
  
  // Helper function to parse date string in local timezone (YYYY-MM-DD format)
  const parseLocalDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const day = parseInt(parts[2], 10);
    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return null;
    return date;
  };

  // Booking data
  const [bookingData, setBookingData] = useState<BookingData>({
    selectedServices: [],
    selectedProfessionals: {},
    // URL-prefill is applied only after we validate the day isn't closed (see effects below).
    selectedDate: null,
    selectedTime: '',
    totalPrice: 0,
    totalDuration: 0,
    insistOnProfessional: {},
    serviceAddress: {
      address: '',
      city: '',
      state: '',
      zip: '',
      locationNotes: '',
      locationLabel: ''
    },
    rememberServiceAddress: false
  });
  const [recurringSettings, setRecurringSettings] = useState<{
    enabled: boolean;
    mode: RecurrenceMode;
    intervalWeeks: number;
    occurrenceCount: number;
  }>({
    enabled: false,
    mode: 'STRICT',
    intervalWeeks: 1,
    occurrenceCount: 10
  });
  const [recurringPreview, setRecurringPreview] = useState<RecurringPreviewResponse | null>(null);
  const [loadingRecurringPreview, setLoadingRecurringPreview] = useState(false);
  const [recurringPreviewError, setRecurringPreviewError] = useState<string | null>(null);
  
  // Update booking data
  // NOTE: Declared early because some effects depend on it.
  const updateBookingData = (updates: Partial<BookingData>) => {
    setBookingData(prev => {
      const updated = { ...prev, ...updates };
      
      // Recalculate totals
      const totalPrice = updated.selectedServices.reduce((sum, service) => sum + Number((service as any)?.price ?? 0), 0);
      const totalDuration = updated.selectedServices.reduce((sum, service) => sum + service.durationMinutes, 0);
      
      return {
        ...updated,
        totalPrice,
        totalDuration
      };
    });

    // Recurring preview must be re-generated when key booking inputs change.
    if (recurringSettings.enabled) {
      if (
        Object.prototype.hasOwnProperty.call(updates, 'selectedServices') ||
        Object.prototype.hasOwnProperty.call(updates, 'selectedProfessionals') ||
        Object.prototype.hasOwnProperty.call(updates, 'selectedDate') ||
        Object.prototype.hasOwnProperty.call(updates, 'selectedTime') ||
        Object.prototype.hasOwnProperty.call(updates, 'insistOnProfessional')
      ) {
        setRecurringPreview(null);
        setRecurringPreviewError(null);
      }
    }
  };
  // Step 1 validity: add-ons cannot be booked by themselves; must include at least 1 non-add-on service.
  // Be defensive: some API payloads may use `is_add_on` instead of `isAddOn`.
  const hasAtLeastOneNonAddOnServiceSelected = bookingData.selectedServices.some((s: any) => {
    const isAddOn = s?.isAddOn ?? s?.is_add_on;
    return isAddOn !== true;
  });
  const [timeConflict, setTimeConflict] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Hold URL-prefill until we can validate it against business hours + closures.
  const pendingPrefillRef = useRef<{ date: Date; time?: string } | null>(null);
  const didAttemptPrefillRef = useRef(false);

  // Capture URL params (date/time) as a pending prefill and align month so closures load.
  useEffect(() => {
    if (!prefilledDate) return;
    const date = parseLocalDate(prefilledDate);
    if (!date) return;

    pendingPrefillRef.current = { date, ...(prefilledTime ? { time: prefilledTime } : {}) };
    setDatePickerMonth(date);
    setCurrentMonth(date);
  }, [prefilledDate, prefilledTime]);

  // Update date picker month when selected date changes
  useEffect(() => {
    if (bookingData.selectedDate) {
      setDatePickerMonth(bookingData.selectedDate);
    }
  }, [bookingData.selectedDate]);

  // Apply URL prefill only if the day isn't closed (operating hours + all-day closures).
  // If closed, we do nothing (no auto-advance) to preserve existing business logic.
  useEffect(() => {
    if (didAttemptPrefillRef.current) return;
    const pending = pendingPrefillRef.current;
    if (!pending) return;

    // Don't overwrite user-driven selection.
    if (bookingData.selectedDate) {
      didAttemptPrefillRef.current = true;
      pendingPrefillRef.current = null;
      return;
    }

    // Wait until operating hours are loaded, and closures fetch for the month has completed.
    if (!operatingHours || operatingHours.length === 0) return;
    if (loadingClosures) return;

    const date = pending.date;

    // Block past days
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (dateOnly < today) {
      didAttemptPrefillRef.current = true;
      pendingPrefillRef.current = null;
      return;
    }

    // Check weekly operating hours closed
    const dayOfWeek = date.getDay();
    const dayOperatingHours = operatingHours.find((h: any) => {
      const dow = (h?.dayOfWeek ?? h?.day_of_week);
      return Number(dow) === dayOfWeek;
    });
    const isClosedByHours = dayOperatingHours
      ? Boolean((dayOperatingHours as any).isClosed ?? (dayOperatingHours as any).is_closed)
      : true;
    if (!dayOperatingHours || isClosedByHours) {
      didAttemptPrefillRef.current = true;
      pendingPrefillRef.current = null;
      return;
    }

    // Check all-day closures (date-only comparison to avoid timezone shifting)
    const dateString = formatLocalYmd(date);
    const toYmd = (v: any): string | null => {
      if (!v) return null;
      if (typeof v === 'string') {
        const s = v.trim();
        if (!s) return null;
        return s.includes('T') ? s.split('T')[0] : s;
      }
      if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10);
      return null;
    };
    const checkMonth = date.getMonth() + 1;
    const checkDay = date.getDate();
    const checkMonthDay = checkMonth * 100 + checkDay;

    const hasAllDayClosure = (businessClosures || []).some((closure: any) => {
      const closureStartYmd = toYmd(closure?.startDate);
      const closureEndYmd = toYmd(closure?.endDate);
      if (!closureStartYmd || !closureEndYmd) return false;

      let isInRange = false;
      if (closure?.repeatYearly) {
        const startParts = closureStartYmd.split('-').map(Number);
        const endParts = closureEndYmd.split('-').map(Number);
        if (startParts.length < 3 || endParts.length < 3) return false;
        const startMonthDay = startParts[1] * 100 + startParts[2];
        const endMonthDay = endParts[1] * 100 + endParts[2];
        if (startParts[1] <= endParts[1]) {
          isInRange = checkMonthDay >= startMonthDay && checkMonthDay <= endMonthDay;
        } else {
          isInRange = checkMonthDay >= startMonthDay || checkMonthDay <= endMonthDay;
        }
      } else {
        isInRange = dateString >= closureStartYmd && dateString <= closureEndYmd;
      }

      return Boolean(isInRange && closure?.closureType === 'all_day');
    });

    if (hasAllDayClosure) {
      didAttemptPrefillRef.current = true;
      pendingPrefillRef.current = null;
      return;
    }

    // Valid prefilled date/time.
    updateBookingData({
      selectedDate: date,
      ...(pending.time ? { selectedTime: pending.time } : {})
    });
    didAttemptPrefillRef.current = true;
    pendingPrefillRef.current = null;
  }, [bookingData.selectedDate?.getTime(), operatingHours, businessClosures, loadingClosures, updateBookingData]);

  // Load business data
  useEffect(() => {
    const fetchData = async () => {
      if (!currentBusiness?.id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch business details
        const businessData = await apiService.getBusiness(currentBusiness.shortName || '');
        const businessEntity = businessData?.business ?? businessData;
        setBusiness({
          ...businessData,
          timezone: businessEntity?.timezone || businessData?.timezone
        });
        
        // Fetch business branding for theme
        try {
          const brandingData = await apiService.getBusinessBranding(currentBusiness.shortName || '');
          setBranding(brandingData);
        } catch (error) {
          console.error('Error fetching branding:', error);
          setBranding(null);
        }
        
        // Fetch services
        const allServices = await apiService.getAllServices(currentBusiness.id);
        const activeServices = allServices.filter((s: any) => s.isActive !== false && s.deleted !== true);
        setServices(activeServices);
        
        // Fetch categories
        try {
          const categoriesData = await apiService.getServiceCategories(currentBusiness.id);
          setCategories(categoriesData || []);
        } catch (error) {
          console.error('Error fetching categories:', error);
          setCategories([]);
        }
        
        // Fetch operating hours
        try {
          const hours = await apiService.getOperatingHours(currentBusiness.id);
          setOperatingHours(hours || []);
        } catch (error) {
          console.error('Error fetching operating hours:', error);
          setOperatingHours([]);
        }
        
        // Fetch professionals using shortName (same as customer booking page)
        try {
          const professionalsData = await apiService.getBusinessProfessionalsByShortName(currentBusiness.shortName || '');
          setProfessionals(professionalsData || []);
          
          // Fetch professional capabilities
          if (professionalsData && professionalsData.length > 0 && currentBusiness.id) {
            fetchProfessionalCapabilities(professionalsData, currentBusiness.id);
          }
        } catch (profError: any) {
          // If professionals endpoint fails (404 or other), fall back to assigning the logged-in user
          console.warn('Could not load professionals (this is okay):', profError);
          setProfessionals([]);
        }
        
      } catch (error: any) {
        console.error('Error loading business data:', error);
        setError(error.message || 'Failed to load business data');
        toast.error('Failed to load business data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentBusiness?.id, currentBusiness?.shortName]);

  // Fetch professional capabilities
  const fetchProfessionalCapabilities = async (profs: User[], businessId: string) => {
    if (profs.length === 0 || !businessId) return;
    
    setLoadingCapabilities(true);
    try {
      const capabilityPromises = profs.map(async (professional) => {
        try {
          const capabilities = await apiService.getProfessionalServices(businessId, professional.id);
          return { professionalId: professional.id, capabilities };
        } catch (error) {
          console.error(`Error fetching capabilities for ${professional.id}:`, error);
          return { professionalId: professional.id, capabilities: [] };
        }
      });
      
      const results = await Promise.all(capabilityPromises);
      const capabilitiesMap: {[professionalId: string]: any[]} = {};
      
      results.forEach(result => {
        capabilitiesMap[result.professionalId] = result.capabilities;
      });
      
      setProfessionalCapabilities(capabilitiesMap);
    } catch (error) {
      console.error('Error fetching professional capabilities:', error);
    } finally {
      setLoadingCapabilities(false);
    }
  };

  // Check if professional has capability for a service
  const hasServiceCapability = (professionalId: string, serviceId: string): boolean => {
    const capabilities = professionalCapabilities[professionalId] || [];
    if (capabilities.length === 0) {
      // If capabilities haven't loaded or are empty, we don't know, so return false to show warning
      return false;
    }
    return capabilities.some((capability: any) => {
      const capId = capability.id || capability.serviceId || capability.service_id;
      return String(capId) === String(serviceId);
    });
  };

  // Check if a professional can perform all selected services
  const canPerformAllServices = (professionalId: string): boolean => {
    if (bookingData.selectedServices.length === 0) return true;
    return bookingData.selectedServices.every(service => hasServiceCapability(professionalId, service.id));
  };

  // Mirror consumer booking eligibility rules:
  // - must be allowed to perform services
  // - must have capability (once loaded)
  // - must have a schedule (once schedules are loaded)
  const isScheduleEligible = (professionalId: string): boolean => {
    // While schedules are still loading, don't exclude anyone (matches BookingStep2 behavior).
    if (loadingSchedules) return true;
    const schedule = professionalSchedules[professionalId];
    return Array.isArray(schedule) && schedule.length > 0;
  };

  const getEligibleProfessionalsForService = (serviceId: string): User[] | null => {
    // Don't make bypass decisions until we actually know capabilities + schedules.
    if (loadingCapabilities || loadingSchedules) return null;

    return professionals.filter(prof => {
      if (prof.canPerformServices === false) return false;
      if (!isScheduleEligible(prof.id)) return false;
      return hasServiceCapability(prof.id, serviceId);
    });
  };

  const getEligibleProfessionalsForAllSelectedServices = (): User[] | null => {
    if (bookingData.selectedServices.length === 0) return [];
    if (loadingCapabilities || loadingSchedules) return null;

    return professionals.filter(prof => {
      if (prof.canPerformServices === false) return false;
      if (!isScheduleEligible(prof.id)) return false;
      return canPerformAllServices(prof.id);
    });
  };

  /**
   * Implements the bypass rules (matches consumer flow):
   * - Bypass ONLY when every selected service has 0 or 1 eligible professionals
   * - Show selection if ANY selected service has 2+ eligible professionals
   */
  const shouldBypassProfessionalSelection = (): boolean | null => {
    if (bookingData.selectedServices.length === 0) return false;
    if (loadingCapabilities || loadingSchedules) return null;

    return bookingData.selectedServices.every(service => {
      const eligible = getEligibleProfessionalsForService(service.id);
      // eligible should never be null here (we gated on loading flags), but keep it safe.
      const count = eligible ? eligible.length : 0;
      return count <= 1;
    });
  };

  // Search for customers with fuzzy search (scoped to customers tied to this business)
  const searchCustomers = async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setCustomerSearchResults([]);
      return;
    }

    const businessId = currentBusiness?.id || business?.id;
    if (!businessId) {
      setCustomerSearchResults([]);
      return;
    }
    
    setSearchingCustomers(true);
    try {
      // Fetch a larger set of results from backend (supports email, name, and phone)
      // Scoped to customers with appointments, quotes, or invoices for this business
      // Then apply fuzzy search on client side for better matching
      const backendResults = await apiService.searchUsers(query, 100, { businessId });
      
      // Apply fuzzy search to rank and filter results
      const fuzzyResults = fuzzySearchCustomers(backendResults, query);
      
      // Limit to top 10 results after fuzzy search
      setCustomerSearchResults(fuzzyResults.slice(0, 10) as User[]);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  };

  // Handle customer search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (customerSearch && !selectedCustomer) {
        searchCustomers(customerSearch);
      } else {
        setCustomerSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [customerSearch]);

  // Handle email lookup in manual customer form
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (showNewCustomerForm && manualCustomer.email && manualCustomer.email.includes('@')) {
        checkEmailInForm(manualCustomer.email);
      } else {
        setEmailLookupResults([]);
        setShowEmailLookupDropdown(false);
        setHasUnselectedEmailMatch(false);
        setMatchedEmail('');
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [manualCustomer.email, showNewCustomerForm]);

  // Handle click outside to close date picker popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isDatePickerOpen && !target.closest('.date-picker-container')) {
        setIsDatePickerOpen(false);
      }
    };

    if (isDatePickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDatePickerOpen]);

  // Re-check match state when manual customer data changes
  useEffect(() => {
    if (hasUnselectedEmailMatch && emailLookupResults.length > 0 && manualCustomer.email) {
      const matchedUser = emailLookupResults[0];
      const currentDataMatches = 
        manualCustomer.firstName === matchedUser.firstName &&
        manualCustomer.lastName === matchedUser.lastName &&
        manualCustomer.email.toLowerCase().trim() === matchedUser.email.toLowerCase();
      
      if (currentDataMatches) {
        // User has manually entered matching data, clear the flag
        setHasUnselectedEmailMatch(false);
      }
    }
  }, [manualCustomer.firstName, manualCustomer.lastName, manualCustomer.email, hasUnselectedEmailMatch, emailLookupResults]);

  // Auto-focus firstname input when new customer form is shown
  useEffect(() => {
    if (showNewCustomerForm && firstNameInputRef.current) {
      // Small delay to ensure the form is rendered
      setTimeout(() => {
        firstNameInputRef.current?.focus();
      }, 100);
    }
  }, [showNewCustomerForm]);

  // Check email in manual customer form
  const checkEmailInForm = async (email: string) => {
    if (!email.trim() || !email.includes('@')) {
      setEmailLookupResults([]);
      setShowEmailLookupDropdown(false);
      setHasUnselectedEmailMatch(false);
      setMatchedEmail('');
      return;
    }
    
    setCheckingEmail(true);
    try {
      const result = await apiService.checkEmailExists(email.toLowerCase().trim());
      if (result.exists && result.user) {
        const matchedUser = {
          id: result.user.id,
          email: result.user.email,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          phone: (result.user as any).phone ?? (result.user as any).phoneNumber ?? undefined
        } as User;
        
        setEmailLookupResults([matchedUser]);
        setShowEmailLookupDropdown(true);
        setMatchedEmail(email.toLowerCase().trim());
        
        // Check if the current manual customer data matches the found user
        // If all fields match, user has already selected the match
        // Otherwise, there's an unselected match
        const currentDataMatches = 
          manualCustomer.firstName === matchedUser.firstName &&
          manualCustomer.lastName === matchedUser.lastName &&
          manualCustomer.email.toLowerCase().trim() === matchedUser.email.toLowerCase();
        
        setHasUnselectedEmailMatch(!currentDataMatches);
      } else {
        setEmailLookupResults([]);
        setShowEmailLookupDropdown(false);
        setHasUnselectedEmailMatch(false);
        setMatchedEmail('');
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailLookupResults([]);
      setShowEmailLookupDropdown(false);
      setHasUnselectedEmailMatch(false);
      setMatchedEmail('');
    } finally {
      setCheckingEmail(false);
    }
  };

  // Check if user is a professional (not an owner)
  const isProfessionalUser = user?.canPerformServices && !user?.roles?.some(role => 
    role.isActive && 
    role.permissions?.some(permission => 
      ['manage_roles', 'manage_professionals', 'manage_settings'].includes(permission)
    )
  );

  /** Resolve the logged-in user as a professional assignment (prefer roster entry when present). */
  const getCurrentUserAsProfessional = (): User | undefined => {
    if (!user?.id) return undefined;
    const fromRoster = professionals.find((p) => p.id === user.id);
    if (fromRoster) return fromRoster;
    return {
      id: user.id,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      canPerformServices: user.canPerformServices || true
    } as User;
  };

  // Get auto-selected professional name when selection UI is bypassed
  const getAutoSelectedProfessionalName = (): string | null => {
    if (isProfessionalUser || bookingData.selectedServices.length === 0) {
      return null;
    }

    const bypass = shouldBypassProfessionalSelection();
    if (bypass !== true) return null;

    const firstService = bookingData.selectedServices[0];
    const selectedProfessional = bookingData.selectedProfessionals[firstService?.id];

    if (selectedProfessional) {
      return `${selectedProfessional.firstName} ${selectedProfessional.lastName.charAt(0)}.`;
    }

    const currentUserAsProfessional = getCurrentUserAsProfessional();
    if (currentUserAsProfessional) {
      return `${currentUserAsProfessional.firstName} ${currentUserAsProfessional.lastName.charAt(0)}.`;
    }
    return null;
  };

  /**
   * Business booking: professionals shown for assignment.
   * Includes everyone with a schedule, plus always the logged-in user (even without schedule/capability).
   * Does NOT filter by service capability — business may override.
   */
  const getProfessionalsForBusinessAssignment = (): User[] => {
    const list = professionals.filter((prof) => {
      if (isProfessionalUser && user?.id) {
        return prof.id === user.id;
      }
      if (user?.id && prof.id === user.id) {
        return true;
      }
      if (prof.canPerformServices === false) {
        return false;
      }
      if (loadingSchedules) {
        return true;
      }
      const schedule = professionalSchedules[prof.id];
      return Array.isArray(schedule) && schedule.length > 0;
    });

    const currentUserAsProfessional = getCurrentUserAsProfessional();
    if (
      currentUserAsProfessional &&
      !isProfessionalUser &&
      !list.some((p) => p.id === currentUserAsProfessional.id)
    ) {
      return [...list, currentUserAsProfessional];
    }
    return list;
  };

  // Handle service selection
  const handleServicesChange = (services: Service[]) => {
    const newProfessionals: { [serviceId: string]: User | undefined } = {};
    
    // If user is a professional, auto-select themselves for all services
    if (isProfessionalUser && user?.id) {
      const currentUserAsProfessional = getCurrentUserAsProfessional();
      
      if (currentUserAsProfessional) {
        services.forEach(service => {
          newProfessionals[service.id] = currentUserAsProfessional;
        });
      }
    } else {
      // For owners, use default behavior (auto-selection will happen in useEffect)
      // If "same professional for all" is enabled and a professional is already chosen,
      // ensure newly added services inherit that professional.
      const chosenProfessional =
        sameProfessionalForAll
          ? services
              .map(s => bookingData.selectedProfessionals[s.id])
              .find((p): p is User => Boolean(p && (p as any).id))
          : undefined;

      const fallbackProfessional = getCurrentUserAsProfessional();
      services.forEach(service => {
        if (chosenProfessional) {
          newProfessionals[service.id] = chosenProfessional;
          return;
        }

        if (bookingData.selectedProfessionals[service.id]) {
          newProfessionals[service.id] = bookingData.selectedProfessionals[service.id];
        } else {
          // Business booking: default to logged-in user (never leave unassigned)
          newProfessionals[service.id] = fallbackProfessional;
        }
      });
    }
    
    updateBookingData({
      selectedServices: services,
      selectedProfessionals: newProfessionals
    });
  };

  // Fetch professional schedules
  const fetchProfessionalSchedules = async (professionalIds: string[], businessId: string) => {
    setLoadingSchedules(true);
    try {
      const schedulePromises = professionalIds.map(async (professionalId) => {
        try {
          const schedules = await apiService.getProfessionalAvailability(businessId, professionalId);
          return { professionalId, schedules };
        } catch (error) {
          console.error(`Error fetching schedule for ${professionalId}:`, error);
          return { professionalId, schedules: [] };
        }
      });
      
      const results = await Promise.all(schedulePromises);
      const schedulesMap: {[professionalId: string]: any[]} = {};
      results.forEach(result => {
        schedulesMap[result.professionalId] = result.schedules;
      });
      setProfessionalSchedules(schedulesMap);
    } catch (error) {
      console.error('Error fetching professional schedules:', error);
    } finally {
      setLoadingSchedules(false);
    }
  };

  // Fetch professional time offs
  const fetchProfessionalTimeOffs = async (professionalIds: string[], businessId: string) => {
    if (professionalIds.length === 0 || !businessId) return;
    
    try {
      const timeOffPromises = professionalIds.map(async (professionalId) => {
        try {
          const timeOffs = await apiService.getProfessionalTimeOffs(businessId, professionalId);
          return { professionalId, timeOffs };
        } catch (error) {
          console.error(`Error fetching time offs for ${professionalId}:`, error);
          return { professionalId, timeOffs: [] };
        }
      });
      
      const results = await Promise.all(timeOffPromises);
      const timeOffsMap: {[professionalId: string]: any[]} = {};
      
      results.forEach(result => {
        timeOffsMap[result.professionalId] = result.timeOffs;
      });
      
      setProfessionalTimeOffs(timeOffsMap);
    } catch (error) {
      console.error('Error fetching professional time offs:', error);
    }
  };

  // Fetch business closures for date range
  const fetchBusinessClosures = async (startDate: Date, endDate: Date) => {
    const businessIdToUse = business?.id || currentBusiness?.id;
    if (!businessIdToUse) return;
    
    setLoadingClosures(true);
    try {
      const startDateStr = formatLocalYmd(startDate);
      const endDateStr = formatLocalYmd(endDate);
      const closures = await apiService.getBusinessClosuresForDateRange(businessIdToUse, startDateStr, endDateStr);
      setBusinessClosures(closures);
    } catch (error) {
      console.error('Error fetching business closures:', error);
      // Don't set error state - closures are optional, continue without them
    } finally {
      setLoadingClosures(false);
    }
  };

  // Check if a date has an all-day time off for any selected professional
  const hasAllDayTimeOff = (date: Date, professionalIds: string[]): boolean => {
    if (professionalIds.length === 0) return false;
    
    const dateStr = formatLocalYmd(date); // YYYY-MM-DD format (local)
    
    return professionalIds.some(professionalId => {
      const timeOffs = professionalTimeOffs[professionalId] || [];
      return timeOffs.some((timeOff: any) => {
        // Extract date part from startDate and endDate
        let startDateStr = String(timeOff.startDate);
        let endDateStr = String(timeOff.endDate);
        
        if (startDateStr.includes('T')) {
          startDateStr = startDateStr.split('T')[0];
        }
        if (endDateStr.includes('T')) {
          endDateStr = endDateStr.split('T')[0];
        }
        
        // Check if date falls within time off range
        if (dateStr < startDateStr || dateStr > endDateStr) {
          return false;
        }
        
        // Check if it's all day
        const isAllDay = timeOff.isAllDay === true || 
          (typeof timeOff.isAllDay === 'string' && timeOff.isAllDay.toLowerCase() === 'true') ||
          (typeof timeOff.isAllDay === 'number' && timeOff.isAllDay === 1);
        
        return isAllDay;
      });
    });
  };

  // Ensure current logged-in user is always available in the professionals list (business booking).
  useEffect(() => {
    if (!user?.id || professionals.find(p => p.id === user.id)) return;
    const currentUserAsProfessional = getCurrentUserAsProfessional();
    if (currentUserAsProfessional) {
      setProfessionals(prev => (prev.some(p => p.id === currentUserAsProfessional.id) ? prev : [...prev, currentUserAsProfessional]));
    }
  }, [user?.id, professionals.length]);

  // Fetch schedules for all professionals when they're loaded AND business is available
  useEffect(() => {
    // Use currentBusiness.id if business.id is not available yet
    const businessIdToUse = business?.id || currentBusiness?.id;

    if (professionals.length > 0 && businessIdToUse) {
      const professionalIds = professionals.map(prof => prof.id);
      if (professionalIds.length > 0) {
        fetchProfessionalSchedules(professionalIds, businessIdToUse);
        fetchProfessionalTimeOffs(professionalIds, businessIdToUse);
      }
    }
  }, [professionals.map(p => p.id).join(','), business?.id, currentBusiness?.id, currentBusiness?.shortName]);

  // Fetch business closures when business is available and month changes
  useEffect(() => {
    const businessIdToUse = business?.id || currentBusiness?.id;
    if (!businessIdToUse) return;
    
    // Calculate date range for current month view (include some buffer)
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    
    // Add buffer to include adjacent months' dates that might be visible
    startDate.setDate(startDate.getDate() - 7);
    endDate.setDate(endDate.getDate() + 7);
    
    fetchBusinessClosures(startDate, endDate);
  }, [business?.id, currentBusiness?.id, currentMonth.getTime()]);

  // Auto-select professional user for all services when they are a professional (not owner)
  useEffect(() => {
    if (isProfessionalUser && user?.id && bookingData.selectedServices.length > 0) {
      const currentUserAsProfessional = getCurrentUserAsProfessional();
      
      if (currentUserAsProfessional) {
        const newProfessionals: { [serviceId: string]: User | undefined } = {};
        let needsUpdate = false;
        
        bookingData.selectedServices.forEach(service => {
          if (bookingData.selectedProfessionals[service.id]?.id !== user.id) {
            newProfessionals[service.id] = currentUserAsProfessional;
            needsUpdate = true;
          } else {
            newProfessionals[service.id] = bookingData.selectedProfessionals[service.id];
          }
        });
        
        if (needsUpdate) {
          updateBookingData({ selectedProfessionals: newProfessionals });
        }
      }
    }
  }, [isProfessionalUser, user?.id, professionals.length, bookingData.selectedServices.length]);

  // Auto-select professional for business owner flow (never leave unassigned / "Any Professional")
  useEffect(() => {
    // Only run for owners, not for professional users
    if (isProfessionalUser || bookingData.selectedServices.length === 0) {
      return;
    }
    if (loadingCapabilities || loadingSchedules) {
      return;
    }

    const newProfessionals: { [serviceId: string]: User | undefined } = {};
    let needsUpdate = false;
    const currentUserAsProfessional = getCurrentUserAsProfessional();
    const bypass = shouldBypassProfessionalSelection();

    // When selection UI is bypassed (every service has 0 or 1 eligible):
    // - 0 eligible => current logged-in user
    // - 1 eligible => that professional
    // When selection UI is shown: fill any missing assignment with the logged-in user.
    bookingData.selectedServices.forEach(service => {
      const eligible = getEligibleProfessionalsForService(service.id);
      if (!eligible) return;

      if (bypass === true) {
        if (eligible.length === 1) {
          if (bookingData.selectedProfessionals[service.id]?.id !== eligible[0].id) {
            newProfessionals[service.id] = eligible[0];
            needsUpdate = true;
          }
        } else if (eligible.length === 0 && currentUserAsProfessional) {
          if (bookingData.selectedProfessionals[service.id]?.id !== currentUserAsProfessional.id) {
            newProfessionals[service.id] = currentUserAsProfessional;
            needsUpdate = true;
          }
        }
      } else if (
        bookingData.selectedProfessionals[service.id] == null &&
        currentUserAsProfessional
      ) {
        newProfessionals[service.id] = currentUserAsProfessional;
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      updateBookingData({ selectedProfessionals: { ...bookingData.selectedProfessionals, ...newProfessionals } });
    }
  }, [
    bookingData.selectedServices,
    bookingData.selectedProfessionals,
    professionals,
    professionalSchedules,
    loadingCapabilities,
    loadingSchedules,
    sameProfessionalForAll,
    isProfessionalUser,
    user?.id,
    updateBookingData
  ]);

  // When arriving from business calendar professional column slot click: if the column's pro is in the eligible list, auto-select them (UI only; no backend change).
  // Re-apply when selected services change and no professional is selected (e.g. user deselected services then selected again).
  useEffect(() => {
    if (!prefillProfessionalId || bookingData.selectedServices.length === 0 || loadingCapabilities || loadingSchedules) {
      return;
    }
    const eligible = getEligibleProfessionalsForAllSelectedServices();
    if (!eligible || eligible.length === 0) return;
    const prefillPro = eligible.find(
      (p) => p.id === prefillProfessionalId || (p as User & { userId?: string }).userId === prefillProfessionalId
    );
    if (!prefillPro) return;
    // Only apply when no professional is selected for the current services (don't overwrite user's choice)
    const hasSelection = bookingData.selectedServices.some((s) => bookingData.selectedProfessionals[s.id] != null);
    if (hasSelection) return;
    const next: { [serviceId: string]: User | undefined } = {};
    bookingData.selectedServices.forEach((s) => {
      next[s.id] = prefillPro;
    });
    updateBookingData({ selectedProfessionals: { ...bookingData.selectedProfessionals, ...next } });
  }, [
    prefillProfessionalId,
    bookingData.selectedServices,
    bookingData.selectedProfessionals,
    loadingCapabilities,
    loadingSchedules,
    updateBookingData
  ]);

  // Handle professional selection
  const handleProfessionalsChange = (professionals: { [serviceId: string]: User | undefined }) => {
    updateBookingData({ selectedProfessionals: professionals });
    
    // Clear insistOnProfessional when professional is deselected or changed
    const updatedInsist = { ...bookingData.insistOnProfessional };
    // Check all selected services to clear/reset insistOnProfessional
    bookingData.selectedServices.forEach(service => {
      const professional = professionals[service.id];
      if (!professional) {
        // Clear insist flag when professional is cleared
        delete updatedInsist[service.id];
      } else {
        // Reset when a different professional is selected
        updatedInsist[service.id] = false;
      }
    });
    updateBookingData({ insistOnProfessional: updatedInsist });
    
    // Fetch availability for selected professionals (schedules should already be loaded)
    const selectedProfessionalIds = Object.values(professionals)
      .filter(prof => prof && prof.id)
      .map(prof => prof!.id);
    
    if (selectedProfessionalIds.length === 0) {
      setAvailabilityData({});
    } else if (bookingData.selectedDate) {
      fetchAvailability(bookingData.selectedDate, true, {
        professionalIds: selectedProfessionalIds,
        selectedProfessionals: professionals
      });
    }
  };
  
  // Fetch availability for a specific date - EXACTLY like BookingStep3.tsx
  const fetchAvailability = async (
    date: Date,
    forceRefresh: boolean = false,
    options?: {
      professionalIds?: string[];
      selectedProfessionals?: { [serviceId: string]: User | undefined };
    }
  ) => {
    const selectedProfessionals = options?.selectedProfessionals ?? bookingData.selectedProfessionals;
    const professionalIds = options?.professionalIds?.length
      ? options.professionalIds
      : Object.values(selectedProfessionals)
          .filter((professional): professional is User => professional !== undefined && professional.id !== undefined)
          .map((professional) => professional.id);
    if (professionalIds.length === 0) return;
    
    // Use currentBusiness.id if business.id is not available yet
    const businessId = business?.id || currentBusiness?.id;
    if (!businessId) {
      return;
    }
    
    const dateString = formatLocalYmd(date);
    const cacheKey = availabilityCacheKey(
      dateString,
      professionalIds,
      bookingData.totalDuration,
      displayTimeZone
    );
    
    // Don't fetch if we already have the data (unless forcing refresh)
    // Force refresh when totalDuration changes, as availability depends on service duration
    if (!forceRefresh && availabilityData[cacheKey]) return;
    
    const fetchSeq = ++availabilityFetchSeqRef.current;
    setLoadingAvailability(true);

    try {
      // Sequential assumption: services in a single appointment are performed in order (not parallel).
      // Only fetch sequential availability when ALL selected services have an explicit professional selected.
      const orderedAssignments = (bookingData.selectedServices || []).map((s: any) => ({
        professionalId: selectedProfessionals?.[s.id]?.id,
        durationMinutes: s.durationMinutes
      }));
      const canUseSequential =
        orderedAssignments.length > 0 &&
        orderedAssignments.every(a => typeof a.professionalId === 'string' && a.professionalId && Number(a.durationMinutes) > 0);

      const availability = canUseSequential
        ? await apiService.getSequentialMultiServiceAvailability(
            businessId,
            dateString,
            orderedAssignments as Array<{ professionalId: string; durationMinutes: number }>,
            displayTimeZone
          )
        : await apiService.getMultiProfessionalAvailability(
            businessId,
            professionalIds,
            dateString,
            bookingData.totalDuration,
            displayTimeZone
          );

      setAvailabilityData(prev => ({
        ...prev,
        [cacheKey]: availability
      }));
    } catch (error) {
      console.error('[OwnerBookingPage] Error fetching availability:', error);
      toast.error('Failed to fetch availability. Please try again.');
    } finally {
      if (fetchSeq === availabilityFetchSeqRef.current) {
        setLoadingAvailability(false);
      }
    }
  };
  
  // Get unique professional IDs from selected professionals
  const getSelectedProfessionalIds = (): string[] => {
    return Object.values(bookingData.selectedProfessionals)
      .filter((professional): professional is User => professional !== undefined && professional.id !== undefined)
      .map(professional => professional.id);
  };

  const getAvailabilityForDate = (dateYmd: string, professionalIds: string[] = getSelectedProfessionalIds()) => {
    return availabilityData[
      availabilityCacheKey(dateYmd, professionalIds, bookingData.totalDuration, displayTimeZone)
    ];
  };

  const getAvailabilitySlotForTime = (availability: any, time: string) => {
    if (!availability || !Array.isArray(availability.timeSlots)) return undefined;
    const wanted = parseClockTimeToMinutes(time);
    return availability.timeSlots.find(
      (slot: any) => parseClockTimeToMinutes(String(slot?.time || '')) === wanted
    );
  };

  const getOriginalBusinessHoursRangeForDate = (
    date: Date | null,
    professionalIds: string[]
  ): { startMinutes: number; endMinutes: number } | null => {
    if (!date) return null;

    const dayOfWeek = date.getDay();

    if (professionalIds.length > 0) {
      let minStart = Infinity;
      let maxEnd = -Infinity;

      professionalIds.forEach((profId) => {
        const schedule = professionalSchedules[profId];
        if (!schedule || schedule.length === 0) return;

        schedule.forEach((s: any) => {
          const scheduleDayOfWeek = Number(s.dayOfWeek !== undefined ? s.dayOfWeek : s.day_of_week);
          if (
            scheduleDayOfWeek !== dayOfWeek ||
            s.isAvailable === false ||
            s.isBreakTime === true
          ) {
            return;
          }
          const startTime = s.startTime || s.start_time;
          const endTime = s.endTime || s.end_time;
          if (!startTime || !endTime) return;
          minStart = Math.min(minStart, parseClockTimeToMinutes(startTime));
          maxEnd = Math.max(maxEnd, parseClockTimeToMinutes(endTime));
        });
      });

      if (minStart !== Infinity && maxEnd !== -Infinity) {
        return convertHoursRangeToTimeZone(
          formatLocalYmd(date),
          { startMinutes: minStart, endMinutes: maxEnd },
          businessTimeZone,
          displayTimeZone
        );
      }
    }

    const dayOperatingHours = operatingHours.find((h) => h.dayOfWeek === dayOfWeek);
    if (dayOperatingHours && !dayOperatingHours.isClosed && dayOperatingHours.openTime && dayOperatingHours.closeTime) {
      return convertHoursRangeToTimeZone(
        formatLocalYmd(date),
        {
          startMinutes: parseClockTimeToMinutes(dayOperatingHours.openTime),
          endMinutes: parseClockTimeToMinutes(dayOperatingHours.closeTime)
        },
        businessTimeZone,
        displayTimeZone
      );
    }

    return null;
  };

  // Fetch availability for current month when professionals or totalDuration change
  useEffect(() => {
    const professionalIds = getSelectedProfessionalIds();
    
    if (professionalIds.length > 0 && business?.id) {
      // Fetch availability for all dates in the current month
      // Force refresh when totalDuration changes, as availability depends on service duration
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const fetchMonthAvailability = async () => {
        const forceRefresh = true; // Always refresh when dependencies change
        for (let day = 1; day <= daysInMonth; day++) {
          const date = new Date(year, month, day);
          if (date >= today) {
            await fetchAvailability(date, forceRefresh);
          }
        }
      };
      
      fetchMonthAvailability();
    }
  }, [JSON.stringify(bookingData.selectedProfessionals), currentMonth.getTime(), business?.id, bookingData.totalDuration, displayTimeZone]);

  // Fetch availability when selected professionals, date, or totalDuration change - EXACTLY like BookingStep3.tsx
  useEffect(() => {
    const businessId = business?.id || currentBusiness?.id;
    if (bookingData.selectedDate && getSelectedProfessionalIds().length > 0 && businessId) {
      fetchAvailability(bookingData.selectedDate, true);
    }
  }, [bookingData.selectedDate?.getTime(), JSON.stringify(bookingData.selectedProfessionals), bookingData.totalDuration, business?.id, currentBusiness?.id, displayTimeZone]);

  // Generate calendar days for date picker popup
  const generateCalendarDays = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  // Handle date selection from calendar popup
  const handleDatePickerSelect = (date: Date) => {
    handleDateChange(date);
    setIsDatePickerOpen(false);
  };

  // Handle date/time selection - EXACTLY like BookingStep3.tsx
  const handleDateChange = (date: Date | null) => {
    updateBookingData({ selectedDate: date });
    // Clear selected time when changing date
    if (bookingData.selectedTime) {
      handleTimeChange('');
    }
    // Fetch availability for the selected date - EXACTLY like BookingStep3.tsx
    const businessId = business?.id || currentBusiness?.id;
    if (date && getSelectedProfessionalIds().length > 0 && businessId) {
      fetchAvailability(date, true);
    }
  };

  const handleTimeChange = (time: string) => {
    updateBookingData({ selectedTime: time });
  };

  // Handle time conflict
  const handleTimeConflictChange = (hasConflict: boolean) => {
    setTimeConflict(hasConflict);
  };

  // At least one selected service is ON_SITE (requires service address)
  const hasOnSiteService = React.useMemo(() => {
    return bookingData.selectedServices.some((s: Service) => {
      const raw = (s as any).serviceLocation || (s as any).service_location;
      const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
      return loc === 'ON_SITE' || loc === 'ONSITE';
    });
  }, [bookingData.selectedServices]);

  // Validate form
  const isFormValid = () => {
    if (!selectedCustomer && (!manualCustomer.firstName)) {
      return false;
    }
    // Check if there's an unselected email match (only if email was provided)
    // Re-check if form data now matches the found user (in case user manually typed matching data)
    if (hasUnselectedEmailMatch && emailLookupResults.length > 0 && manualCustomer.email && manualCustomer.email.trim()) {
      const matchedUser = emailLookupResults[0];
      const currentDataMatches = 
        manualCustomer.firstName === matchedUser.firstName &&
        manualCustomer.lastName === matchedUser.lastName &&
        manualCustomer.email.toLowerCase().trim() === matchedUser.email.toLowerCase();
      
      if (currentDataMatches) {
        // User has manually entered matching data, clear the flag
        setHasUnselectedEmailMatch(false);
      } else {
        // Still doesn't match, prevent submission
        return false;
      }
    }
    if (bookingData.selectedServices.length === 0) {
      return false;
    }
    // Validate professional selection for all services (business flow: never allow unassigned)
    if (bookingData.selectedServices.length > 0 && !loadingCapabilities && !loadingSchedules) {
      if (sameProfessionalForAll) {
        const firstService = bookingData.selectedServices[0];
        const selectedProfessional = bookingData.selectedProfessionals[firstService.id];
        if (!selectedProfessional) {
          return false;
        }
        // Ensure the same professional is applied to every selected service
        const allMatch = bookingData.selectedServices.every(
          (service) => bookingData.selectedProfessionals[service.id]?.id === selectedProfessional.id
        );
        if (!allMatch) {
          return false;
        }
      } else {
        for (const service of bookingData.selectedServices) {
          const selectedProfessional = bookingData.selectedProfessionals[service.id];
          if (!selectedProfessional) {
            return false;
          }
        }
      }
    }
    if (!bookingData.selectedDate || !bookingData.selectedTime) {
      return false;
    }
    if (timeConflict) {
      return false;
    }
    if (recurringSettings.enabled) {
      if (!recurringPreview || !Array.isArray((recurringPreview as any).occurrences)) {
        return false;
      }
      if ((recurringPreview as any).occurrences.some((o: any) => o?.status === 'CONFLICT')) {
        return false;
      }
    }
    // When at least one selected service is ON_SITE, require full service address
    if (hasOnSiteService) {
      const a = bookingData.serviceAddress;
      if (!a || !a.address?.trim() || !a.city?.trim() || !a.state?.trim() || !a.zip?.trim()) {
        return false;
      }
    }
    return true;
  };

  const to24HourStartTime = (raw: string): string | null => {
    const s = String(raw || '').trim();
    if (!s) return null;
    // Already HH:mm
    if (/^\d{2}:\d{2}$/.test(s)) return s;

    // Parse "h:mm AM/PM" or "hh:mm AM/PM"
    const m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const period = String(m[3]).toUpperCase();
    if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    if (h < 0 || h > 23 || min < 0 || min > 59) return null;
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  };

  const canPreviewRecurring = (): { ok: boolean; reason?: string } => {
    if (!recurringSettings.enabled) return { ok: false, reason: 'Recurring is disabled' };
    const businessId = business?.id || currentBusiness?.id;
    if (!businessId) return { ok: false, reason: 'Missing business' };
    if (!bookingData.selectedDate || !bookingData.selectedTime) return { ok: false, reason: 'Select a date and time first' };
    if (!Array.isArray(bookingData.selectedServices) || bookingData.selectedServices.length === 0) return { ok: false, reason: 'Select at least one service' };

    const startTime = to24HourStartTime(bookingData.selectedTime);
    if (!startTime) return { ok: false, reason: 'Invalid time format' };

    if (recurringSettings.mode === 'STRICT') {
      const missingPro = bookingData.selectedServices.some((s: any) => !bookingData.selectedProfessionals?.[s.id]?.id);
      if (missingPro) {
        return { ok: false, reason: 'Select a professional for every service to preview a recurring series.' };
      }
    }

    return { ok: true };
  };

  const handlePreviewRecurring = async () => {
    const gate = canPreviewRecurring();
    if (!gate.ok) {
      setRecurringPreviewError(gate.reason || 'Cannot preview recurring schedule');
      setRecurringPreview(null);
      return;
    }

    const businessId = business?.id || currentBusiness?.id;
    if (!businessId) return;
    if (!user?.id) {
      setRecurringPreviewError('Login required');
      setRecurringPreview(null);
      return;
    }

    const startDate = formatLocalYmd(bookingData.selectedDate!);
    const startTime = to24HourStartTime(bookingData.selectedTime)!;

    setLoadingRecurringPreview(true);
    setRecurringPreviewError(null);
    try {
      const assignments = (bookingData.selectedServices || []).map((s: any) => ({
        serviceId: s.id,
        durationMinutes: Number(s.durationMinutes) || 0,
        professionalId: bookingData.selectedProfessionals?.[s.id]?.id,
        insistOnProfessional: bookingData.insistOnProfessional?.[s.id] === true
      }));

      // NOTE: Preview endpoint currently enforces customerId === authenticated user.
      // Preview does not depend on customer identity, so for staff booking we use the actor userId.
      const resp = await apiService.previewRecurringAppointments({
        businessId,
        customerId: user.id,
        mode: recurringSettings.mode,
        intervalWeeks: Number(recurringSettings.intervalWeeks) || 1,
        occurrenceCount: Number(recurringSettings.occurrenceCount) || 1,
        startDate,
        startTime,
        clientTimeZone: displayTimeZone,
        assignments
      } as any);

      setRecurringPreview(resp);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to preview recurring schedule';
      setRecurringPreviewError(String(msg));
      setRecurringPreview(null);
    } finally {
      setLoadingRecurringPreview(false);
    }
  };

  const renderRecurringSection = () => {
    const enabled = recurringSettings.enabled;
    const summary = (() => {
      const preview = recurringPreview;
      if (!enabled) return null;
      if (!preview || !Array.isArray((preview as any).occurrences)) return null;
      const occurrences = (preview as any).occurrences as any[];
      const available = occurrences.filter(o => o?.status === 'AVAILABLE').length;
      const conflicts = occurrences.filter(o => o?.status === 'CONFLICT').length;
      const last = occurrences[occurrences.length - 1];
      const lastDateLabel = last?.scheduledAt
        ? new Date(last.scheduledAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : null;
      return { available, conflicts, lastDateLabel, total: occurrences.length };
    })();

    return (
      <div className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Repeat className="w-4 h-4 text-gray-600" aria-hidden />
            <h3 className="text-base font-semibold text-gray-900">Recurring (Optional)</h3>
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer select-none max-w-xl">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => {
                const nextEnabled = e.target.checked;
                setRecurringSettings((prev) => ({ ...prev, enabled: nextEnabled }));
                setRecurringPreview(null);
                setRecurringPreviewError(null);
              }}
              className="w-4 h-4 mt-0.5 shrink-0 border-gray-300 rounded"
              style={{ accentColor: '#3B82F6' }}
            />
            <span className="text-sm text-gray-700 leading-snug">
              Schedule multiple appointments as a recurring series.
            </span>
          </label>
        </div>

        {!enabled ? null : (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-4">
            <div
              className="
                grid grid-cols-1 gap-4
                md:grid-cols-2 md:gap-x-4 md:gap-y-4
                lg:grid-cols-[auto_auto_auto] lg:gap-x-3 lg:gap-y-0
                items-start
              "
            >
              <div className="w-full min-w-0 shrink-0 lg:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Repeat every</label>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="number"
                    min={1}
                    max={8}
                    value={recurringSettings.intervalWeeks}
                    onChange={(e) => {
                      const n = Math.max(1, Math.min(8, Number(e.target.value) || 1));
                      setRecurringSettings((prev) => ({ ...prev, intervalWeeks: n }));
                      setRecurringPreview(null);
                      setRecurringPreviewError(null);
                    }}
                    className="w-20 shrink-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  <span className="text-sm text-gray-700 whitespace-nowrap">week(s)</span>
                </div>
              </div>

              <div className="w-full min-w-0 shrink-0 lg:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Occurrences</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={recurringSettings.occurrenceCount}
                  onChange={(e) => {
                    const n = Math.max(1, Math.min(10, Number(e.target.value) || 1));
                    setRecurringSettings((prev) => ({ ...prev, occurrenceCount: n }));
                    setRecurringPreview(null);
                    setRecurringPreviewError(null);
                  }}
                  className="w-full md:max-w-[6.5rem] lg:w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Up to 10 occurrences.</p>
              </div>

              <div className="w-full min-w-0 md:col-span-2 lg:col-span-1 flex flex-col lg:block">
                <label className="block text-sm font-medium text-gray-700 mb-1 invisible pointer-events-none max-lg:hidden" aria-hidden="true">
                  Preview
                </label>
                <button
                  type="button"
                  onClick={handlePreviewRecurring}
                  disabled={loadingRecurringPreview}
                  className="aacore-btn2-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto lg:w-full whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingRecurringPreview ? (
                    <>
                      <Loader2 className="w-4 h-4 shrink-0 animate-spin" aria-hidden />
                      Previewing…
                    </>
                  ) : (
                    <>
                      <CalendarDays className="w-4 h-4 shrink-0" aria-hidden />
                      Preview schedule
                    </>
                  )}
                </button>
              </div>
            </div>

            {recurringPreviewError ? (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
                {recurringPreviewError}
              </div>
            ) : null}

            {summary ? (
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-800">
                <div className="font-semibold mb-1">Preview summary</div>
                <div>
                  {summary.available} available, {summary.conflicts} conflicts (out of {summary.total})
                  {summary.lastDateLabel ? ` • Last: ${summary.lastDateLabel}` : ''}
                </div>
                {summary.conflicts > 0 ? (
                  <div className="text-xs text-gray-600 mt-1">
                    Resolve conflicts (change time/interval) and preview again before creating the series.
                  </div>
                ) : null}
              </div>
            ) : null}

            {enabled && recurringPreview && Array.isArray((recurringPreview as any).occurrences) ? (
              <div className="border-t border-gray-200 pt-4">
                <div className="text-sm font-semibold text-gray-900 mb-2">Occurrences</div>
                <div className="space-y-2">
                  {(recurringPreview as any).occurrences.map((o: any) => {
                    const d = o?.scheduledAt ? new Date(o.scheduledAt) : null;
                    const label = d && !Number.isNaN(d.getTime())
                      ? d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) +
                        ' • ' +
                        d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                      : (o?.scheduledAt || 'Unknown time');
                    const ok = o?.status === 'AVAILABLE';
                    return (
                      <div key={String(o?.occurrenceIndex)} className={`flex items-center justify-between p-2 rounded border ${ok ? 'bg-white border-gray-200' : 'bg-red-50 border-red-200'}`}>
                        <div className="text-sm text-gray-900">
                          <span className="font-semibold mr-2">#{o?.occurrenceIndex}</span>
                          {label}
                        </div>
                        <div className={`text-xs font-bold px-2 py-0.5 rounded-full ${ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {ok ? 'Available' : 'Conflict'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  // Submit booking
  const handleSubmit = async () => {
    if (!isFormValid()) {
      if (hasUnselectedEmailMatch && emailLookupResults.length > 0 && manualCustomer.email && manualCustomer.email.trim()) {
        toast.error('Please select the matching customer from the dropdown or enter a different email address');
      } else if (!selectedCustomer && !manualCustomer.firstName) {
        toast.error('Please select a customer or enter at least a first name');
      } else if (bookingData.selectedServices.length === 0) {
        toast.error('Please select at least one service');
      } else if (!bookingData.selectedDate || !bookingData.selectedTime) {
        toast.error('Please select a date and time');
      } else if (timeConflict) {
        toast.error('The selected time has a conflict. Please choose another time.');
      } else if (hasOnSiteService) {
        const a = bookingData.serviceAddress;
        if (!a || !a.address?.trim() || !a.city?.trim() || !a.state?.trim() || !a.zip?.trim()) {
          toast.error('Please enter the service address for the on-site appointment.');
        }
      }
      return;
    }
    
    // Use currentBusiness.id if business.id is not available yet
    const businessId = business?.id || currentBusiness?.id;
    if (!businessId) {
      toast.error('Business information is missing. Please try again.');
      return;
    }

    setSubmitting(true);
    try {
      let customerId = selectedCustomer?.id;
      
      // If no customer selected, try to find or create one
      if (!customerId) {
        // If email is provided, check if customer exists
        if (manualCustomer.email && manualCustomer.email.trim()) {
          const emailCheck = await apiService.checkEmailExists(manualCustomer.email.trim().toLowerCase());
          if (emailCheck.exists && emailCheck.user) {
            customerId = emailCheck.user.id;
          }
        }
        
        // If customer still doesn't exist, create them
        if (!customerId) {
          try {
            const invitationResult = await apiService.createCustomerInvitation({
              email: manualCustomer.email?.trim().toLowerCase() || undefined,
              firstName: manualCustomer.firstName,
              lastName: manualCustomer.lastName || undefined,
              phone: manualCustomer.phone || undefined,
              businessId: businessId
            });
            
            customerId = invitationResult.userId;
            
            if (invitationResult.emailSent) {
              toast.success(`Customer created and invitation email sent to ${manualCustomer.email}`);
            } else if (manualCustomer.email && manualCustomer.email.trim()) {
              toast.success(`Customer created, but invitation email failed to send. Customer ID: ${customerId}`);
            } else {
              toast.success(`Customer created successfully (no email provided)`);
            }
          } catch (error: any) {
            console.error('Error creating customer invitation:', error);
            toast.error(error.response?.data?.message || 'Failed to create customer. Please try again.');
            setSubmitting(false);
            return;
          }
        }
      }

      if (!customerId) {
        toast.error('Customer information is required');
        setSubmitting(false);
        return;
      }

      // Create assignments array
      const chosenProfessionalId =
        sameProfessionalForAll
          ? bookingData.selectedServices
              .map(service => bookingData.selectedProfessionals[service.id]?.id)
              .find((id): id is string => typeof id === 'string' && id.length > 0)
          : undefined;

      const assignments = bookingData.selectedServices.map(service => ({
        serviceId: service.id,
        professionalId: sameProfessionalForAll
          ? chosenProfessionalId
          : bookingData.selectedProfessionals[service.id]?.id,
        durationMinutes: service.durationMinutes,
        notes: undefined, // Don't set default notes
        insistOnProfessional: bookingData.insistOnProfessional[service.id] === true
      }));

      const customerName = selectedCustomer 
        ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`
        : `${manualCustomer.firstName} ${manualCustomer.lastName}`;

      // Parse time from 12-hour format (e.g., "6:30 PM") to 24-hour format
      const parseTime = (timeStr: string): { hours: number; minutes: number } => {
        const [timePart, period] = timeStr.split(' ');
        const [hours, minutes] = timePart.split(':').map(Number);
        let hour24 = hours;
        if (period) {
          if (period.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
          if (period.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;
        }
        return { hours: hour24, minutes };
      };

      const { hours, minutes } = parseTime(bookingData.selectedTime);
      const startTime24 = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

      if (recurringSettings.enabled) {
        // Require a successful, conflict-free preview before creating recurring series
        if (!recurringPreview || !Array.isArray((recurringPreview as any).occurrences)) {
          toast.error('Please preview the recurring schedule before creating the series.');
          setSubmitting(false);
          return;
        }
        if ((recurringPreview as any).occurrences.some((o: any) => o?.status === 'CONFLICT')) {
          toast.error('One or more recurring occurrences have a conflict. Please adjust and preview again.');
          setSubmitting(false);
          return;
        }

        const startDate = formatLocalYmd(bookingData.selectedDate!);

        const result = await apiService.createRecurringAppointments({
          businessId: businessId,
          customerId: customerId,
          mode: recurringSettings.mode,
          intervalWeeks: recurringSettings.intervalWeeks,
          occurrenceCount: recurringSettings.occurrenceCount,
          startDate,
          startTime: startTime24,
          clientTimeZone: displayTimeZone,
          ...(hasOnSiteService && bookingData.serviceAddress && bookingData.serviceAddress.address?.trim() && bookingData.serviceAddress.city?.trim() && bookingData.serviceAddress.state?.trim() && bookingData.serviceAddress.zip?.trim()
            ? {
                serviceAddress: bookingData.serviceAddress,
                rememberServiceAddress: bookingData.rememberServiceAddress === true
              }
            : {}),
          assignments: assignments,
        } as any);

        const count = Array.isArray((result as any).appointmentIds) ? (result as any).appointmentIds.length : recurringSettings.occurrenceCount;
        toast.success(`Recurring series created successfully! Scheduled ${count} appointment(s).`);
      } else {
        // Create appointment data
        const appointmentData = {
          businessId: businessId,
          customerId: customerId,
          scheduledAt: localDateAndTimeToUtcIso(bookingData.selectedDate!, startTime24),
          notes: undefined, // Don't set default notes unless explicitly specified
          bookingContext: 'business' as const,
          // Business dashboard booking flow: allow owners/professionals to proceed even on unavailable slots.
          allowOverride: true,
          assignments: assignments,
          ...(hasOnSiteService && bookingData.serviceAddress && bookingData.serviceAddress.address?.trim() && bookingData.serviceAddress.city?.trim() && bookingData.serviceAddress.state?.trim() && bookingData.serviceAddress.zip?.trim()
            ? {
                serviceAddress: bookingData.serviceAddress,
                rememberServiceAddress: bookingData.rememberServiceAddress === true
              }
            : {})
        };

        // Create the appointment
        await apiService.createAppointment(appointmentData);
        toast.success('Appointment created successfully!');
      }
      
      // Save private notes if they were entered
      if (privateNotes.trim()) {
        try {
          await apiService.updateCustomerPrivateNotes(customerId, businessId, privateNotes.trim());
        } catch (error: any) {
          console.error('Error saving private notes:', error);
          // Don't fail the appointment creation if notes fail to save
          toast.error('Appointment created, but failed to save private notes');
        }
      }
      
      // Build URL params to preserve filter state
      const urlParams = new URLSearchParams();
      urlParams.set('section', fromMyAppointments ? 'my-appointments' : 'appointments');
      
      // Preserve filter state if it was passed in
      if (fromMyAppointments) {
        // My Appointments filters
        if (preservedMyAppointmentsFilters.myDateRangeType) {
          urlParams.set('myDateRangeType', preservedMyAppointmentsFilters.myDateRangeType);
        }
        if (preservedMyAppointmentsFilters.myDateRangeStart) {
          urlParams.set('myDateRangeStart', preservedMyAppointmentsFilters.myDateRangeStart);
        }
        if (preservedMyAppointmentsFilters.myStatus) {
          urlParams.set('myStatus', preservedMyAppointmentsFilters.myStatus);
        }
        if (preservedMyAppointmentsFilters.myView) {
          urlParams.set('myView', preservedMyAppointmentsFilters.myView);
        }
      } else {
        // Regular Appointments filters
        if (preservedAppointmentsFilters.dateRangeType) {
          urlParams.set('dateRangeType', preservedAppointmentsFilters.dateRangeType);
        }
        if (preservedAppointmentsFilters.dateRangeStart) {
          urlParams.set('dateRangeStart', preservedAppointmentsFilters.dateRangeStart);
        }
        if (preservedAppointmentsFilters.status) {
          urlParams.set('status', preservedAppointmentsFilters.status);
        }
        if (preservedAppointmentsFilters.professionalId) {
          urlParams.set('professionalId', preservedAppointmentsFilters.professionalId);
        }
        if (preservedAppointmentsFilters.view) {
          urlParams.set('view', preservedAppointmentsFilters.view);
        }
      }
      
      // Navigate back to appointments page (or my-appointments if coming from there) with preserved filters
      setTimeout(() => {
        navigate(`/dashboard/business?${urlParams.toString()}`);
      }, 1000);
      
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to create appointment');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle notification bell click
  const handleNotificationsClick = () => {
    navigate('/dashboard/business?section=notifications');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <NavigationBar 
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={handleNotificationsClick}
        />
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <NavigationBar 
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={handleNotificationsClick}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg font-medium text-gray-900 mb-2">{error}</p>
            <button
              onClick={() => navigate(fromMyAppointments ? '/dashboard/business?section=my-appointments' : '/dashboard/business?section=appointments')}
              className="text-blue-600 hover:text-blue-800"
            >
              Go Back
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 no-animations flex flex-col">
      <NavigationBar 
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={handleNotificationsClick}
      />
      
      <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr]">
        <BusinessDashboardSidebar
          isSidebarOpen={isSidebarOpen}
          isSidebarCollapsed={isSidebarCollapsed}
          activeSection={undefined}
          onClose={() => setIsSidebarOpen(false)}
          onToggleCollapse={toggleSidebar}
          onSectionChange={(section) => {
            navigate(`/dashboard/business?section=${section}`);
          }}
          unreadNotificationCount={unreadNotifications.length}
        />
        
        {/* Main Content */}
        <div className="flex flex-col flex-1">
          {/* Mobile Header */}
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="flex-1 text-center text-lg font-semibold text-gray-900">Create New Appointment</h1>
              {/* Invisible spacer to balance hamburger button and center the title */}
              <div className="w-10 h-10"></div>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 p-4 md:p-8">
            {/* Header */}
            <div className="mb-8">
              <button
                onClick={() => navigate(fromMyAppointments ? '/dashboard/business?section=my-appointments' : '/dashboard/business?section=appointments')}
                className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {fromMyAppointments ? 'Back to My Appointments' : 'Back to Appointments'}
              </button>
              <div className="hidden md:block">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">Create New Appointment</h2>
                <p className="text-gray-600 mt-1">Book an appointment on behalf of a customer</p>
              </div>
            </div>

        <div className="md:bg-white md:rounded-lg md:shadow-sm md:border md:border-gray-200 md:p-6 space-y-8">
          {/* Customer Selection Section */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#3B82F620' }}
              >
                <UserIcon className="w-4 h-4" style={{ color: '#3B82F6' }} />
              </div>
              <h2 className="text-xl text-gray-900">Select Customer</h2>
            </div>
            
            {selectedCustomer ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                    {selectedCustomer.firstName?.[0]}{selectedCustomer.lastName?.[0]}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {selectedCustomer.firstName} {selectedCustomer.lastName}
                    </p>
                    <p className="text-sm text-gray-600 flex items-center gap-2 mt-1 min-w-0">
                      <i className="bi bi-envelope flex-shrink-0"></i>
                      <span className="truncate">{formatCustomerEmail(selectedCustomer.email)}</span>
                    </p>
                    {!!getCustomerPhone(selectedCustomer) && (
                      <p className="text-sm text-gray-600 flex items-center gap-2 mt-1 min-w-0">
                        <i className="bi bi-telephone flex-shrink-0"></i>
                        <span className="truncate">{getCustomerPhone(selectedCustomer)}</span>
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedCustomer(null);
                    setCustomerSearch('');
                    setCustomerSearchResults([]);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={(e) => {
                        setCustomerSearch(e.target.value);
                        setShowCustomerDropdown(true);
                        setShowNewCustomerForm(false);
                      }}
                      onFocus={() => setShowCustomerDropdown(true)}
                      placeholder="Search name, phone or email"
                      autoComplete="off"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck="false"
                      name="customer-search"
                      id="customer-search-input"
                      className={`w-full pl-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${customerSearch ? 'pr-10' : 'pr-4'}`}
                    />
                    {customerSearch && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCustomerSearch('');
                          setCustomerSearchResults([]);
                          setShowCustomerDropdown(false);
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                        aria-label="Clear search"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowNewCustomerForm(true);
                      setCustomerSearch('');
                      setCustomerSearchResults([]);
                      setShowCustomerDropdown(false);
                    }}
                    className="aacore-btn2-primary whitespace-nowrap"
                    title="New Customer"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span className="hidden md:inline">New Customer</span>
                  </button>
                </div>
                
                {showCustomerDropdown && (customerSearchResults.length > 0 || searchingCustomers) && (
                  <div className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {searchingCustomers ? (
                      <div className="px-4 py-3 text-gray-500 text-sm">Searching...</div>
                    ) : customerSearchResults.length > 0 ? (
                      customerSearchResults.map((customer) => (
                        <div
                          key={customer.id}
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setCustomerSearch(`${customer.firstName} ${customer.lastName} (${formatCustomerEmail(customer.email)})`);
                            setShowCustomerDropdown(false);
                            setCustomerSearchResults([]);
                          }}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                            {customer.firstName?.[0]}{customer.lastName?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {customer.firstName} {customer.lastName}
                            </p>
                            <p className="text-sm text-gray-600 flex items-center gap-2 mt-1 min-w-0">
                              <i className="bi bi-envelope flex-shrink-0"></i>
                              <span className="truncate">{formatCustomerEmail(customer.email)}</span>
                            </p>
                            {!!getCustomerPhone(customer) && (
                              <p className="text-sm text-gray-600 flex items-center gap-2 mt-1 min-w-0">
                                <i className="bi bi-telephone flex-shrink-0"></i>
                                <span className="truncate">{getCustomerPhone(customer)}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    ) : customerSearch.length >= 2 ? (
                      <div className="px-4 py-3 text-gray-500 text-sm">
                        No customers found. You can still create the appointment and the customer will be notified.
                      </div>
                    ) : null}
                  </div>
                )}
                
                {(showNewCustomerForm || (customerSearch.length >= 2 && !selectedCustomer && customerSearchResults.length === 0 && !searchingCustomers)) && (
                  <form autoComplete="off" className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-500">
                        {showNewCustomerForm ? 'Enter customer details:' : 'Customer not found. Enter customer details manually:'}
                      </p>
                      {showNewCustomerForm && (
                        <button
                          onClick={() => {
                            setShowNewCustomerForm(false);
                            setManualCustomer({ firstName: '', lastName: '', email: '', phone: '' });
                          }}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        ref={firstNameInputRef}
                        type="text"
                        placeholder="First Name"
                        value={manualCustomer.firstName}
                        onChange={(e) => setManualCustomer({ ...manualCustomer, firstName: e.target.value })}
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck="false"
                        name="customer-first-name"
                        id="customer-first-name"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Last Name (optional)"
                        value={manualCustomer.lastName}
                        onChange={(e) => setManualCustomer({ ...manualCustomer, lastName: e.target.value })}
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck="false"
                        name="customer-surname"
                        id="customer-surname-input"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Email (optional)"
                          value={manualCustomer.email}
                          onChange={(e) => {
                            const newEmail = e.target.value.toLowerCase();
                            setManualCustomer({ ...manualCustomer, email: newEmail });
                            
                            // Only show dropdown if email is provided and contains @
                            if (newEmail && newEmail.includes('@')) {
                              setShowEmailLookupDropdown(true);
                            } else {
                              setShowEmailLookupDropdown(false);
                              setEmailLookupResults([]);
                            }
                            
                            // Clear match state if email changed to something different
                            if (matchedEmail && newEmail.toLowerCase().trim() !== matchedEmail) {
                              setHasUnselectedEmailMatch(false);
                              setMatchedEmail('');
                            }
                          }}
                          onFocus={() => {
                            if (emailLookupResults.length > 0) {
                              setShowEmailLookupDropdown(true);
                            }
                          }}
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          spellCheck="false"
                          name="customer-email-manual"
                          id="customer-email-manual"
                          className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full ${
                            hasUnselectedEmailMatch ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                        />
                        {hasUnselectedEmailMatch && (
                          <p className="mt-1 text-sm text-red-600">
                            A customer with this email already exists. Please select them from the dropdown above or enter a different email.
                          </p>
                        )}
                        {checkingEmail && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          </div>
                        )}
                        {showEmailLookupDropdown && emailLookupResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                            {emailLookupResults.map((customer) => (
                              <div
                                key={customer.id}
                                onClick={() => {
                                  // Set as selected customer (same as search bar selection)
                                  setSelectedCustomer(customer);
                                  setCustomerSearch(`${customer.firstName} ${customer.lastName} (${formatCustomerEmail(customer.email)})`);
                                  
                                  // Clear manual customer form
                                  setManualCustomer({
                                    firstName: '',
                                    lastName: '',
                                    email: '',
                                    phone: ''
                                  });
                                  
                                  // Close the new customer form
                                  setShowNewCustomerForm(false);
                                  
                                  // Clear email lookup states
                                  setShowEmailLookupDropdown(false);
                                  setEmailLookupResults([]);
                                  setHasUnselectedEmailMatch(false);
                                  setMatchedEmail('');
                                  
                                  // Clear customer dropdown
                                  setShowCustomerDropdown(false);
                                  setCustomerSearchResults([]);
                                }}
                                className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                              >
                                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                                  {customer.firstName?.[0]}{customer.lastName?.[0]}
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">
                                    {customer.firstName} {customer.lastName}
                                  </p>
                                  <p className="text-sm text-gray-600">{formatCustomerEmail(customer.email)}</p>
                                  {customer.phone && (
                                    <p className="text-xs text-gray-500">{displayCustomerPhone(customer.phone, canViewFullCustomerPhone(user, currentBusiness?.id))}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder="Phone (optional)"
                        value={manualCustomer.phone}
                        onChange={(e) => setManualCustomer({ ...manualCustomer, phone: e.target.value })}
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck="false"
                        name="customer-phone"
                        id="customer-phone"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>

          {/* Service Selection */}
          <div>
            <BookingStep1
              services={services}
              selectedServices={bookingData.selectedServices}
              onServicesChange={handleServicesChange}
              preselectedServiceId={null}
              categories={categories}
              audience="professional"
              businessId={currentBusiness?.id || business?.id}
              selectedProfessionals={bookingData.selectedProfessionals}
              selectedDate={bookingData.selectedDate}
              selectedTime={bookingData.selectedTime}
            />
          </div>

          {/* Professional Selection - Custom for Staff */}
          {hasAtLeastOneNonAddOnServiceSelected && isProfessionalUser && fromMyAppointments && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>You will be assigned</strong> as the professional for all selected services.
              </p>
            </div>
          )}
          {hasAtLeastOneNonAddOnServiceSelected && !(isProfessionalUser && fromMyAppointments) && (() => {
            // Use the SAME bypass logic as consumer booking:
            // determine eligible professionals based on capability + schedule (once loaded).
            // Only hide this section when there is NO actual choice.
            const bypass = shouldBypassProfessionalSelection();
            if (bypass === true) {
              return null;
            }
            
            return (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Professionals</h2>
                {(() => {
                // Get primary color from business branding
                const processedBranding = branding ? {
                  ...branding,
                  ...(branding.useCustomColors ? {} : getLayoutThemeColors(branding.layout || 'modern'))
                } : null;
                const themeStyles = getThemeStyles(processedBranding);
                const primaryColor = themeStyles?.inlineStyles?.buttonPrimary.backgroundColor || '#3B82F6';
                
                return sameProfessionalForAll ? (
                // Single selection for all services
                <>
                  <label className="flex items-center gap-2 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      checked={sameProfessionalForAll}
                      onChange={(e) => {
                        setSameProfessionalForAll(e.target.checked);
                        const fallbackProfessional = getCurrentUserAsProfessional();
                        if (!e.target.checked) {
                          // Keep each service assigned (default to logged-in user) — no unassigned state
                          const nextProfessionals: { [serviceId: string]: User | undefined } = {};
                          bookingData.selectedServices.forEach(service => {
                            nextProfessionals[service.id] =
                              bookingData.selectedProfessionals[service.id] || fallbackProfessional;
                          });
                          handleProfessionalsChange(nextProfessionals);
                        } else {
                          // When checking, apply the same professional to all services
                          const firstService = bookingData.selectedServices[0];
                          const selectedProfessional =
                            bookingData.selectedProfessionals[firstService.id] || fallbackProfessional;
                          if (selectedProfessional) {
                            const sameProfessionals: { [serviceId: string]: User | undefined } = {};
                            bookingData.selectedServices.forEach(service => {
                              sameProfessionals[service.id] = selectedProfessional;
                            });
                            handleProfessionalsChange(sameProfessionals);
                          }
                        }
                      }}
                      className="w-4 h-4 border-gray-300 rounded"
                      style={{
                        accentColor: primaryColor,
                        color: primaryColor
                      }}
                    />
                    <span className="text-sm font-medium text-gray-700">Same Professional for all services</span>
                  </label>
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Select a Professional</h3>
                    <p className="text-gray-600 text-sm">Choose a professional for all selected services</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Individual professionals (business may assign anyone, including logged-in user) */}
                    {loadingSchedules ? (
                      <div className="col-span-full flex items-center justify-center py-8">
                        <div className="flex items-center space-x-3">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          <span className="text-sm text-gray-600">Loading professional schedules...</span>
                        </div>
                      </div>
                    ) : (
                      orderProfessionalsForDisplay(getProfessionalsForBusinessAssignment()).map((professional) => {
                          const firstService = bookingData.selectedServices[0];
                          const isSelected = bookingData.selectedProfessionals[firstService.id]?.id === professional.id;
                          const isInsisted = firstService && bookingData.insistOnProfessional[firstService.id] === true;
                          const initials = `${professional.firstName?.[0] || ''}${professional.lastName?.[0] || ''}`.toUpperCase();
                          const canDoAll = canPerformAllServices(professional.id);
                          
                          // For professional users, make it read-only (always selected)
                          const isReadOnly = isProfessionalUser && user?.id === professional.id;
                          
                          return (
                            <div
                              key={professional.id}
                              className={`relative bg-white border-2 rounded-lg p-4 transition-all ${
                                isReadOnly 
                                  ? 'cursor-not-allowed opacity-75' 
                                  : 'cursor-pointer hover:shadow-md'
                              } ${
                                isSelected 
                                  ? 'shadow-sm border-blue-600' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              onClick={() => {
                                if (!isReadOnly) {
                                  const sameProfessionals: { [serviceId: string]: User | undefined } = {};
                                  bookingData.selectedServices.forEach(service => {
                                    sameProfessionals[service.id] = professional;
                                  });
                                  handleProfessionalsChange(sameProfessionals);
                                }
                              }}
                            >
                              {/* Checkbox at top-right corner */}
                              <div 
                                className="absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                                style={isSelected 
                                  ? { borderColor: '#3B82F6', backgroundColor: '#3B82F6' } 
                                  : { borderColor: '#D1D5DB' }
                                }
                              >
                                {isSelected && (
                                  <CheckCircle className="w-3 h-3 text-white" />
                                )}
                              </div>
                              {/* Heart icon for insist on professional - only show when selected */}
                              {isSelected && firstService && !isReadOnly && (
                                <button
                                  onClick={(e) => {
                                    const updated = { ...bookingData.insistOnProfessional };
                                    // Apply to all services when sameProfessionalForAll is true
                                    bookingData.selectedServices.forEach(service => {
                                      updated[service.id] = !isInsisted;
                                    });
                                    updateBookingData({ insistOnProfessional: updated });
                                    e.stopPropagation();
                                  }}
                                  className={`absolute top-3 right-10 w-5 h-5 rounded-full flex items-center justify-center transition-all z-10 ${
                                    isInsisted 
                                      ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                                      : 'text-gray-400 hover:text-red-500 bg-white hover:bg-gray-50'
                                  }`}
                                  aria-label={isInsisted ? 'Remove insist on professional' : 'Insist on this professional'}
                                  title={isInsisted ? 'Remove insist on professional' : 'Insist on this professional'}
                                >
                                  <Heart 
                                    className={`w-3.5 h-3.5 ${isInsisted ? 'fill-current' : ''}`}
                                  />
                                </button>
                              )}
                              <div className="flex items-start space-x-3">
                                <div 
                                  className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                                  style={isSelected 
                                    ? { backgroundColor: '#3B82F6' } 
                                    : { backgroundColor: '#9CA3AF' }
                                  }
                                >
                                  {initials || <UserIcon className="w-6 h-6" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <h4 className="text-base font-semibold text-gray-900 truncate">
                                        {professional.firstName} {professional.lastName.charAt(0)}.
                                      </h4>
                                      {!canDoAll && (
                                        <span title="This professional may not be assigned to all selected services">
                                          <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {professional.email && (
                                    <p className="text-xs text-gray-500 truncate">{professional.email}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                    )}
                  </div>
                </div>
                </>
              ) : (
                // Per-service selection (existing UI)
                <>
                  <label className="flex items-center gap-2 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      checked={sameProfessionalForAll}
                      onChange={(e) => {
                        setSameProfessionalForAll(e.target.checked);
                        const fallbackProfessional = getCurrentUserAsProfessional();
                        if (!e.target.checked) {
                          const nextProfessionals: { [serviceId: string]: User | undefined } = {};
                          bookingData.selectedServices.forEach(service => {
                            nextProfessionals[service.id] =
                              bookingData.selectedProfessionals[service.id] || fallbackProfessional;
                          });
                          handleProfessionalsChange(nextProfessionals);
                        } else {
                          const firstService = bookingData.selectedServices[0];
                          const selectedProfessional =
                            bookingData.selectedProfessionals[firstService.id] || fallbackProfessional;
                          if (selectedProfessional) {
                            const sameProfessionals: { [serviceId: string]: User | undefined } = {};
                            bookingData.selectedServices.forEach(service => {
                              sameProfessionals[service.id] = selectedProfessional;
                            });
                            handleProfessionalsChange(sameProfessionals);
                          }
                        }
                      }}
                      className="w-4 h-4 border-gray-300 rounded"
                      style={{
                        accentColor: primaryColor,
                        color: primaryColor
                      }}
                    />
                    <span className="text-sm font-medium text-gray-700">Same Professional for all services</span>
                  </label>
                  <div className="space-y-6">
                {bookingData.selectedServices.map((service) => (
                  <div key={service.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{service.name}</h3>
                      <p className="text-gray-600 text-sm">Select a professional for this service</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Individual professionals (no "Any Professional" in business flow) */}
                      {loadingSchedules ? (
                        <div className="col-span-full flex items-center justify-center py-8">
                          <div className="flex items-center space-x-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <span className="text-sm text-gray-600">Loading professional schedules...</span>
                          </div>
                        </div>
                      ) : (
                        orderProfessionalsForDisplay(getProfessionalsForBusinessAssignment()).map((professional) => {
                          const isSelected = bookingData.selectedProfessionals[service.id]?.id === professional.id;
                          const isInsisted = bookingData.insistOnProfessional[service.id] === true;
                          const initials = `${professional.firstName?.[0] || ''}${professional.lastName?.[0] || ''}`.toUpperCase();
                          const hasCapability = hasServiceCapability(professional.id, service.id);
                          
                          // For professional users, make it read-only (always selected)
                          const isReadOnly = isProfessionalUser && user?.id === professional.id;
                          
                          return (
                            <div
                              key={professional.id}
                              className={`relative bg-white border-2 rounded-lg p-4 transition-all ${
                                isReadOnly 
                                  ? 'cursor-not-allowed opacity-75' 
                                  : 'cursor-pointer hover:shadow-md'
                              } ${
                                isSelected 
                                  ? 'shadow-sm border-blue-600' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              onClick={() => {
                                if (!isReadOnly) {
                                  handleProfessionalsChange({
                                    ...bookingData.selectedProfessionals,
                                    [service.id]: professional
                                  });
                                }
                              }}
                            >
                              {/* Checkbox at top-right corner */}
                              <div 
                                className="absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                                style={isSelected 
                                  ? { borderColor: '#3B82F6', backgroundColor: '#3B82F6' } 
                                  : { borderColor: '#D1D5DB' }
                                }
                              >
                                {isSelected && (
                                  <CheckCircle className="w-3 h-3 text-white" />
                                )}
                              </div>
                              {/* Heart icon for insist on professional - only show when selected */}
                              {isSelected && !isReadOnly && (
                                <button
                                  onClick={(e) => {
                                    const updated = { ...bookingData.insistOnProfessional };
                                    updated[service.id] = !isInsisted;
                                    updateBookingData({ insistOnProfessional: updated });
                                    e.stopPropagation();
                                  }}
                                  className={`absolute top-3 right-10 w-5 h-5 rounded-full flex items-center justify-center transition-all z-10 ${
                                    isInsisted 
                                      ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                                      : 'text-gray-400 hover:text-red-500 bg-white hover:bg-gray-50'
                                  }`}
                                  aria-label={isInsisted ? 'Remove insist on professional' : 'Insist on this professional'}
                                  title={isInsisted ? 'Remove insist on professional' : 'Insist on this professional'}
                                >
                                  <Heart 
                                    className={`w-3.5 h-3.5 ${isInsisted ? 'fill-current' : ''}`}
                                  />
                                </button>
                              )}
                              <div className="flex items-start space-x-3">
                                <div 
                                  className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                                  style={isSelected 
                                    ? { backgroundColor: '#3B82F6' } 
                                    : { backgroundColor: '#9CA3AF' }
                                  }
                                >
                                  {initials || <UserIcon className="w-6 h-6" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <h4 className="text-base font-semibold text-gray-900 truncate">
                                        {professional.firstName} {professional.lastName.charAt(0)}.
                                      </h4>
                                      {!hasCapability && (
                                        <span title="This professional may not be assigned to this service">
                                          <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {professional.email && (
                                    <p className="text-xs text-gray-500 truncate">{professional.email}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                ))}
                  </div>
                </>
                );
              })()}
              </div>
            );
          })()}

          {/* Date & Time Selection - Custom for Staff */}
          {hasAtLeastOneNonAddOnServiceSelected && (
            <div className="space-y-6">
              {/* Header */}
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#3B82F620' }}
                  >
                    <Calendar className="w-4 h-4" style={{ color: '#3B82F6' }} />
                  </div>
                  <h2 className="text-xl text-gray-900">
                    {hasPrefilledDateTime ? 'Date & Time' : 'Select Date & Time'}
                  </h2>
                  <div className="relative group flex-shrink-0">
                    <HelpCircle className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 pointer-events-none w-64">
                      <div className="bg-gray-900 text-white text-sm rounded-lg py-2 px-3 shadow-lg">
                        <div className="whitespace-normal text-left">
                          {(() => {
                            const professionalIds = getSelectedProfessionalIds();
                            if (professionalIds.length > 0) {
                              const hasSchedule = professionalIds.some(profId => {
                                const schedule = professionalSchedules[profId];
                                return schedule && schedule.length > 0;
                              });
                              if (!hasSchedule) {
                                return "The selected professional has no work schedule configured. Please select a different professional or configure their schedule first.";
                              }
                              return "Choose the date and time for the appointment. Available dates and times are based on the selected professional's work schedule.";
                            }
                            return "Choose the date and time for the appointment. Available dates are based on business operating hours.";
                          })()}
                        </div>
                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                </div>
                {bookingData.totalDuration > 0 && (
                  <div className="mt-4 flex items-center gap-3 flex-wrap">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      <Clock className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-900">
                        Total duration: <span style={{ color: '#3B82F6' }}>{(() => {
                          const hours = Math.floor(bookingData.totalDuration / 60);
                          const mins = bookingData.totalDuration % 60;
                          if (hours > 0) {
                            return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
                          }
                          return `${mins}m`;
                        })()}</span>
                      </span>
                    </div>
                    {(() => {
                      const professionalName = getAutoSelectedProfessionalName();
                      if (professionalName) {
                        return (
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                            <UserIcon className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">
                              {professionalName}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
              </div>

              {hasPrefilledDateTime ? (
                /* Simplified Date & Time View (Pre-filled from Calendar) */
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                  <div className="space-y-6">
                    {/* Date Input (Editable) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date
                      </label>
                      <div className="relative date-picker-container">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        {/* Desktop: Native date input */}
                        <input
                          type="date"
                          value={bookingData.selectedDate ? formatLocalYmd(bookingData.selectedDate) : ''}
                          onChange={(e) => {
                            const newDate = e.target.value ? parseLocalDate(e.target.value) : null;
                            if (newDate) {
                              handleDateChange(newDate);
                            }
                          }}
                          className="hidden sm:block w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white"
                        />
                        {/* Mobile: Button that opens calendar popup */}
                        <button
                          type="button"
                          onClick={() => {
                            if (bookingData.selectedDate) {
                              setDatePickerMonth(new Date(bookingData.selectedDate));
                            }
                            setIsDatePickerOpen(true);
                          }}
                          className="sm:hidden w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-left text-base bg-white text-gray-900"
                        >
                          {bookingData.selectedDate 
                            ? bookingData.selectedDate.toLocaleDateString('en-US', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })
                            : 'Select date'
                          }
                        </button>
                        
                        {/* Calendar Popup (Mobile) */}
                        {isDatePickerOpen && (
                          <>
                            {/* Mobile: Fixed overlay */}
                            <div className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden" onClick={() => setIsDatePickerOpen(false)}></div>
                            <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 sm:absolute sm:inset-x-auto sm:top-full sm:translate-y-0 sm:left-0 sm:right-auto sm:mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50 min-w-[280px] w-auto sm:w-auto max-w-[calc(100vw-2rem)] sm:max-w-none">
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-medium text-gray-900">Select Date</h3>
                                <button
                                  onClick={() => setIsDatePickerOpen(false)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                              
                              {/* Calendar navigation */}
                              <div className="flex items-center justify-between mb-4">
                                <button
                                  onClick={() => {
                                    const newMonth = new Date(datePickerMonth);
                                    newMonth.setMonth(newMonth.getMonth() - 1);
                                    setDatePickerMonth(newMonth);
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                >
                                  <ChevronLeft className="w-4 h-4 text-gray-600" />
                                </button>
                                <span className="text-sm font-medium text-gray-900">
                                  {datePickerMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                </span>
                                <button
                                  onClick={() => {
                                    const newMonth = new Date(datePickerMonth);
                                    newMonth.setMonth(newMonth.getMonth() + 1);
                                    setDatePickerMonth(newMonth);
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                >
                                  <ChevronRight className="w-4 h-4 text-gray-600" />
                                </button>
                              </div>
                              
                              {/* Calendar grid */}
                              <div className="grid grid-cols-7 gap-1 mb-2">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                  <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                                    {day}
                                  </div>
                                ))}
                              </div>
                              <div className="grid grid-cols-7 gap-1">
                                {generateCalendarDays(
                                  datePickerMonth.getFullYear(),
                                  datePickerMonth.getMonth()
                                ).map((date, index) => {
                                  if (!date) {
                                    return <div key={index} className="h-8"></div>;
                                  }
                                  
                                  const isSelected = bookingData.selectedDate && date.toDateString() === bookingData.selectedDate.toDateString();
                                  const isToday = date.toDateString() === new Date().toDateString();
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  // Past dates stay greyed (with hatch) but remain selectable for business back-dating.
                                  const isPast = date < today;
                                  
                                  return (
                                    <button
                                      key={index}
                                      type="button"
                                      onClick={() => handleDatePickerSelect(date)}
                                      className={`h-8 rounded-md text-sm font-medium transition-all ${
                                        isSelected
                                          ? 'bg-blue-600 text-white shadow-sm'
                                          : isPast
                                          ? 'opacity-80 hover:opacity-100'
                                          : isToday
                                          ? 'bg-blue-50 text-blue-600 border border-blue-200'
                                          : 'hover:bg-gray-100 text-gray-900'
                                      }`}
                                      style={!isSelected && isPast ? PAST_SELECTABLE_SLOT_STYLE : undefined}
                                      title={isPast && !isSelected ? 'Past date (override allowed)' : undefined}
                                    >
                                      {date.getDate()}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Time Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Time
                      </label>
                      {(() => {
                        const professionalIds = getSelectedProfessionalIds();
                        const dateString = bookingData.selectedDate ? formatLocalYmd(bookingData.selectedDate) : '';
                        const availability = dateString ? getAvailabilityForDate(dateString, professionalIds) : null;
                        
                        // Get time slots for the selected date - EXACTLY like the normal time selection
                        // If we have availability data, use ALL time slots from there (including conflicting ones)
                        // The isTimeSlotAvailable function will disable slots without available professionals
                        let timeSlots: string[] = [];
                        let timeSlotsFromAvailability = false;
                        
                        // Check if availability data exists and has time slots
                        // IMPORTANT: If availability exists but has empty timeSlots, we still fall back to schedule
                        // This handles the case where availability was fetched earlier but showed no slots
                        const hasAvailabilityWithSlots = availability && 
                          availability.timeSlots && 
                          Array.isArray(availability.timeSlots) &&
                          availability.timeSlots.length > 0;
                        
                        if (hasAvailabilityWithSlots) {
                          // Return all time slots (not filtered) - conflicting ones will be disabled by isTimeSlotAvailable
                          // Deduplicate time slots in case backend returns duplicates
                          let allTimeSlots = availability.timeSlots.map((slot: any) => slot.time);
                          allTimeSlots = Array.from(new Set(allTimeSlots)).sort() as string[];
                          
                          if (allTimeSlots.length > 0) {
                            allTimeSlots = extendAvailabilityTimeSlots(
                              allTimeSlots,
                              slotStepMinutes,
                              getOriginalBusinessHoursRangeForDate(bookingData.selectedDate, professionalIds),
                              { fillCoreGaps: true }
                            );
                          }
                          
                          timeSlots = allTimeSlots;
                          timeSlotsFromAvailability = true;
                        } else if (loadingAvailability && professionalIds.length > 0) {
                          // If no availability data yet, but we're loading, return loading state
                          return (
                            <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                  <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-sm font-semibold text-yellow-900 mb-1">Loading Availability</h4>
                                  <p className="text-sm text-yellow-800">
                                    Loading available time slots for the selected professional...
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // If we don't have time slots yet (either no availability data or availability has empty slots),
                        // try to generate from schedule as fallback
                        // This handles cases where:
                        // 1. Availability data doesn't exist yet
                        // 2. Availability data exists but has empty timeSlots (from previous fetch)
                        if (timeSlots.length === 0 && professionalIds.length > 0) {
                          // Generate from professional schedule (fallback)
                          if (bookingData.selectedDate) {
                            // Check if schedules are still loading
                            if (loadingSchedules) {
                              return (
                                <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                                  <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0">
                                      <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                      </div>
                                    </div>
                                    <div>
                                      <h4 className="text-sm font-semibold text-yellow-900 mb-1">Loading Schedule</h4>
                                      <p className="text-sm text-yellow-800">
                                        Loading professional schedule...
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            
                            const selectedProfessionalId = professionalIds[0];
                            const schedule = professionalSchedules[selectedProfessionalId];
                            
                            if (!schedule || schedule.length === 0) {
                              return (
                                <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                                  <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0">
                                      <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                      </div>
                                    </div>
                                    <div>
                                      <h4 className="text-sm font-semibold text-yellow-900 mb-1">No Work Schedule</h4>
                                      <p className="text-sm text-yellow-800">
                                        The selected professional has no work schedule configured. Please select a different professional or configure their schedule first.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            
                            const range = getOriginalBusinessHoursRangeForDate(bookingData.selectedDate, professionalIds);
                            if (range) {
                              const extendedStart = Math.max(0, range.startMinutes - 180);
                              const extendedEnd = Math.min(24 * 60 - 1, range.endMinutes + 180);
                              for (let minutes = extendedStart; minutes < extendedEnd; minutes += slotStepMinutes) {
                                const timeStr = minutesToHhmm(minutes);
                                if (timeStr && !timeSlots.includes(timeStr)) {
                                  timeSlots.push(timeStr);
                                }
                              }
                            }
                          }
                        } else {
                          // No professionals selected, use operating hours
                          if (bookingData.selectedDate) {
                            const dayOfWeek = bookingData.selectedDate.getDay();
                            const dayOperatingHours = operatingHours.find(h => h.dayOfWeek === dayOfWeek);
                            
                            if (!dayOperatingHours || dayOperatingHours.isClosed) {
                              return (
                                <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                                  <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0">
                                      <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                      </div>
                                    </div>
                                    <div>
                                      <h4 className="text-sm font-semibold text-yellow-900 mb-1">No Available Times</h4>
                                      <p className="text-sm text-yellow-800">
                                        {!dayOperatingHours 
                                          ? `No operating hours set for this date.`
                                          : `The business is closed on this date.`}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            
                            const range = getOriginalBusinessHoursRangeForDate(bookingData.selectedDate, []);
                            if (range) {
                              const extendedStart = Math.max(0, range.startMinutes - 180);
                              const extendedEnd = Math.min(24 * 60 - 1, range.endMinutes + 180);
                              for (let minutes = extendedStart; minutes < extendedEnd; minutes += slotStepMinutes) {
                                const timeStr = minutesToHhmm(minutes);
                                if (timeStr && !timeSlots.includes(timeStr)) {
                                  timeSlots.push(timeStr);
                                }
                              }
                            }
                          }
                        }
                        
                        // If no time slots available after trying all methods
                        if (timeSlots.length === 0) {
                          // Check if we have availability data that explicitly says no slots
                          // vs. just not having data yet
                          const hasExplicitNoAvailability = availability && 
                            availability.timeSlots && 
                            availability.timeSlots.length === 0 &&
                            availability.availableProfessionals &&
                            availability.availableProfessionals.length === 0;
                          
                          return (
                            <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                  <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-sm font-semibold text-yellow-900 mb-1">No Available Times</h4>
                                  <p className="text-sm text-yellow-800">
                                    {hasExplicitNoAvailability
                                      ? 'No available time slots for this date. Please select a different date.'
                                      : 'No available time slots for this date. Please select a different date.'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // Deduplicate and sort time slots before rendering
                        timeSlots = buildHourDisplayTimeSlots(
                          Array.from(new Set(timeSlots)).sort(),
                          bookingSlotMinutes === 60
                        );
                        
                        const getOriginalBusinessHoursRange = (): { startMinutes: number, endMinutes: number } | null =>
                          getOriginalBusinessHoursRangeForDate(bookingData.selectedDate, professionalIds);
                        
                        // Helper function to check if a time slot is in the extended range (3 hours before/after)
                        const isExtendedHour = (timeStr: string): boolean => {
                          const originalRange = getOriginalBusinessHoursRange();
                          if (!originalRange) return false;
                          
                          // Parse time string to minutes
                          const parseTimeToMinutes = (time: string): number => {
                            // Remove AM/PM if present
                            const [timePart, period] = time.split(' ');
                            const [hours, minutes] = timePart.split(':').map(Number);
                            let hour24 = hours;
                            if (period) {
                              if (period.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
                              if (period.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;
                            }
                            return hour24 * 60 + minutes;
                          };
                          
                          const timeMinutes = parseTimeToMinutes(timeStr);
                          const { startMinutes, endMinutes } = originalRange;
                          
                          // Check if time is outside original range but within extended range (3 hours = 180 minutes)
                          return timeMinutes < startMinutes || timeMinutes >= endMinutes;
                        };
                        
                        // Format time for display (convert from 24h to 12h if needed)
                        const formatTimeDisplay = (timeStr: string): string => {
                          // If already in 12h format, return as is
                          if (timeStr.includes('AM') || timeStr.includes('PM')) {
                            return timeStr;
                          }
                          // Convert from 24h format (HH:MM) to 12h format
                          const [hours, minutes] = timeStr.split(':').map(Number);
                          const period = hours >= 12 ? 'PM' : 'AM';
                          const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
                          return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
                        };
                        
                        // Check if today
                        const today = new Date();
                        const selectedDateOnly = bookingData.selectedDate ? new Date(bookingData.selectedDate.getFullYear(), bookingData.selectedDate.getMonth(), bookingData.selectedDate.getDate()) : null;
                        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                        const isToday = selectedDateOnly && selectedDateOnly.getTime() === todayOnly.getTime();
                        
                        // Check if time slot is available (extended hours are always available)
                        const isTimeSlotAvailable = (time: string) => {
                          // Extended hours are always available
                          if (isExtendedHour(time)) return true;
                          if (!bookingData.selectedDate) return true;
                          
                          // Check if the time has already passed for today
                          if (isToday) {
                            const [hours, minutes] = time.split(':').map(Number);
                            const timeSlot = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
                            const now = new Date();
                            const bufferTime = 15 * 60 * 1000; // 15 minutes
                            if (timeSlot.getTime() <= now.getTime() + bufferTime) {
                              return false;
                            }
                          }
                          
                          // If no professionals selected, all time slots are available
                          if (professionalIds.length === 0) return true;
                          
                          // Always check availability data if it exists, regardless of where time slots came from
                          // This ensures conflicts are detected even when time slots were generated from schedule
                          if (availability && availability.timeSlots && availability.timeSlots.length > 0) {
                            // Normalize time format for comparison (both should be HH:mm, but handle edge cases)
                            const normalizeTime = (t: string) => {
                              // Remove AM/PM if present and convert to 24h
                              if (t.includes('AM') || t.includes('PM')) {
                                const [timePart, period] = t.split(' ');
                                const [hours, minutes] = timePart.split(':').map(Number);
                                let hour24 = hours;
                                if (period?.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
                                if (period?.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;
                                return `${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                              }
                              return t; // Already in 24h format
                            };
                            
                            const normalizedTime = normalizeTime(time);
                            
                            // Check if the time slot exists in availability data
                            const timeSlot = availability.timeSlots.find((slot: any) => {
                              const slotTime = normalizeTime(slot.time);
                              return slotTime === normalizedTime;
                            });
                            
                            if (timeSlot) {
                              return isOwnerDisplaySlotAvailable(timeSlot, professionalIds);
                            }

                            // Missing from the API is not a conflict. The backend omits starts that
                            // cannot fit the selected duration before close; it still returns
                            // appointment overlaps with empty availableProfessionals.
                            return true;
                          }
                          
                          // If no availability data exists yet, trust the schedule
                          // Conflicts will be checked on submit
                          return true;
                        };
                        
                        // Check if time slot has passed
                        const isTimeSlotPassed = (time: string) => {
                          if (!isToday) return false;
                          const [hours, minutes] = time.split(':').map(Number);
                          const timeSlot = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
                          const now = new Date();
                          const bufferTime = 15 * 60 * 1000;
                          return timeSlot.getTime() <= now.getTime() + bufferTime;
                        };
                        
                        return (
                          <>
                            <div className="grid grid-cols-3 gap-2">
                              {timeSlots.map((time: string) => {
                                const isExtended = isExtendedHour(time);
                                const isAvailable = isExtended ? true : isTimeSlotAvailable(time); // Extended hours are always available
                                const isPassed = isTimeSlotPassed(time);
                                const isDurationLeadIn = isDurationLeadInSlot(getAvailabilitySlotForTime(availability, time));
                                const formattedTime = formatTimeDisplay(time);
                                const isSelected = bookingData.selectedTime === formattedTime || bookingData.selectedTime === time;
                                
                                return (
                                  <button
                                    key={time}
                                    type="button"
                                    onClick={() => handleTimeChange(formattedTime)}
                                    className={`relative p-3 rounded-md text-sm font-medium transition-all ${
                                      isSelected
                                        ? 'shadow-sm'
                                        : isPassed
                                        ? 'opacity-80 hover:opacity-100'
                                        : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'
                                    } ${isExtended ? 'italic' : ''}`}
                                    style={
                                      isSelected
                                        ? {
                                            color: '#FFFFFF',
                                            backgroundColor: '#3B82F6',
                                            border: '1px solid #3B82F6',
                                            // Keep the "extended" feel even when selected
                                            ...(isExtended ? { fontStyle: 'italic' as const } : {})
                                          }
                                      : isPassed
                                        ? PAST_SELECTABLE_SLOT_STYLE
                                      : !isAvailable
                                        ? { color: '#991B1B', backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }
                                      : isDurationLeadIn
                                        ? DURATION_LEAD_IN_SLOT_STYLE
                                      : isExtended
                                        ? {
                                            color: '#374151',
                                            backgroundColor: '#F7FEF9', // Even lighter green
                                            border: '1px solid #D1FAE5',
                                            fontStyle: 'italic'
                                          }
                                      : { color: '#374151', backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }
                                    }
                                    title={
                                      isDurationLeadIn && !isPassed && isAvailable
                                        ? DURATION_LEAD_IN_TOOLTIP
                                        : isPassed
                                        ? `Past time (override allowed): ${formattedTime}`
                                        : isExtended
                                        ? `Extended hours - ${formattedTime} (outside normal business hours)`
                                        : !isAvailable
                                        ? `Unavailable slot (override allowed): ${formattedTime}`
                                        : `Select ${formattedTime}`
                                    }
                                  >
                                    {formattedTime}
                                    {isDurationLeadIn && !isPassed && isAvailable && !isSelected && (
                                      <span
                                        className="absolute top-1 right-1 inline-flex items-center justify-center w-5 h-5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                                        title={DURATION_LEAD_IN_TOOLTIP}
                                        aria-label={DURATION_LEAD_IN_TOOLTIP}
                                      >
                                        <AlertTriangle className="w-3 h-3" />
                                      </span>
                                    )}
                                    {!isAvailable && !isPassed && !isExtended && (
                                      <span
                                        className="absolute top-1 right-1 inline-flex items-center justify-center w-5 h-5 rounded bg-red-100 text-red-700 border border-red-200"
                                        title="Unavailable slot"
                                        aria-label="Unavailable slot"
                                      >
                                        <AlertTriangle className="w-3 h-3" />
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Recurring (Optional) */}
                  <div className="pt-2">
                    {renderRecurringSection()}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Date Selection */}
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                  <div className="mb-6">
                    <h3 className="text-lg text-gray-900">Select Date</h3>
                  </div>
                  
                  {/* Calendar Header */}
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                    <button
                      onClick={() => {
                        const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
                        setCurrentMonth(newMonth);
                        // Fetch availability for new month if professionals are selected
                        const professionalIds = getSelectedProfessionalIds();
                        if (professionalIds.length > 0 && business?.id) {
                          // Fetch availability for all dates in the new month
                          const year = newMonth.getFullYear();
                          const month = newMonth.getMonth();
                          const daysInMonth = new Date(year, month + 1, 0).getDate();
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          
                          for (let day = 1; day <= daysInMonth; day++) {
                            const date = new Date(year, month, day);
                            if (date >= today) {
                              fetchAvailability(date, true);
                            }
                          }
                        }
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-opacity text-gray-600 hover:text-gray-900"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <h4 className="text-lg font-semibold text-gray-900">
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h4>
                    <button
                      onClick={() => {
                        const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1);
                        setCurrentMonth(newMonth);
                        // Fetch availability for new month if professionals are selected
                        const professionalIds = getSelectedProfessionalIds();
                        if (professionalIds.length > 0 && business?.id) {
                          // Fetch availability for all dates in the new month
                          const year = newMonth.getFullYear();
                          const month = newMonth.getMonth();
                          const daysInMonth = new Date(year, month + 1, 0).getDate();
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          
                          for (let day = 1; day <= daysInMonth; day++) {
                            const date = new Date(year, month, day);
                            if (date >= today) {
                              fetchAvailability(date, true);
                            }
                          }
                        }
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-opacity text-gray-600 hover:text-gray-900"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>

                  {loadingAvailability && getSelectedProfessionalIds().length > 0 && (
                    <div 
                      className="mb-4 p-3 rounded-lg border"
                      style={{ 
                        backgroundColor: '#3B82F610',
                        borderColor: '#3B82F630'
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div 
                          className="animate-spin rounded-full h-4 w-4 border-b-2"
                          style={{ borderColor: '#3B82F6' }}
                        ></div>
                        <span className="text-sm text-gray-700">Loading availability for this month...</span>
                      </div>
                    </div>
                  )}

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-2 mb-4">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className="p-2 text-center text-xs font-semibold text-gray-600 uppercase">
                        {day}
                      </div>
                    ))}
                    {(() => {
                      const year = currentMonth.getFullYear();
                      const month = currentMonth.getMonth();
                      const firstDay = new Date(year, month, 1);
                      const lastDay = new Date(year, month + 1, 0);
                      const daysInMonth = lastDay.getDate();
                      const startingDayOfWeek = firstDay.getDay();
                      const professionalIds = getSelectedProfessionalIds();
                      
                      const days = [];
                      // Empty cells for days before the first day of the month
                      for (let i = 0; i < startingDayOfWeek; i++) {
                        days.push(null);
                      }
                      
                      // Days of the month
                      for (let day = 1; day <= daysInMonth; day++) {
                        days.push(new Date(year, month, day));
                      }
                      
                      // Helper function to check if professional has any schedule
                      const hasProfessionalSchedule = (): boolean => {
                        if (professionalIds.length === 0) return false;
                        // Check if any selected professional has a schedule
                        return professionalIds.some(profId => {
                          const schedule = professionalSchedules[profId];
                          return schedule && schedule.length > 0;
                        });
                      };
                      
                      // Helper function to check if professional works on a specific day of week
                      const professionalWorksOnDay = (dayOfWeek: number): boolean => {
                        if (professionalIds.length === 0) return false;
                        
                        // Check if any selected professional has a schedule for this day
                        return professionalIds.some(profId => {
                          const schedule = professionalSchedules[profId];
                          if (!schedule || schedule.length === 0) return false;
                          
                          // Check if any schedule entry matches this day and is available
                          return schedule.some((s: any) => {
                            const scheduleDayOfWeek = s.dayOfWeek !== undefined ? s.dayOfWeek : s.day_of_week;
                            return scheduleDayOfWeek === dayOfWeek && 
                                   s.isAvailable !== false && 
                                   s.isBreakTime !== true &&
                                   (s.startTime || s.start_time) && 
                                   (s.endTime || s.end_time);
                          });
                        });
                      };

                      // Find an all-day business closure that applies to the given date (date-only; avoids JS Date timezone shifting).
                      const getAllDayClosureForDate = (date: Date): any | null => {
                        const dateString = formatLocalYmd(date);
                        const checkMonth = date.getMonth() + 1;
                        const checkDay = date.getDate();

                        const toYmd = (v: any): string | null => {
                          if (!v) return null;
                          if (typeof v === 'string') {
                            const s = v.trim();
                            if (!s) return null;
                            return s.includes('T') ? s.split('T')[0] : s;
                          }
                          if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10);
                          return null;
                        };

                        for (const closure of businessClosures) {
                          const closureStartYmd = toYmd((closure as any).startDate);
                          const closureEndYmd = toYmd((closure as any).endDate);
                          if (!closureStartYmd || !closureEndYmd) continue;

                          let isInRange = false;
                          if ((closure as any).repeatYearly) {
                            const startParts = closureStartYmd.split('-').map(Number);
                            const endParts = closureEndYmd.split('-').map(Number);
                            if (startParts.length < 3 || endParts.length < 3) continue;
                            const closureStartMonth = startParts[1];
                            const closureStartDay = startParts[2];
                            const closureEndMonth = endParts[1];
                            const closureEndDay = endParts[2];

                            const checkMonthDay = checkMonth * 100 + checkDay;
                            const startMonthDay = closureStartMonth * 100 + closureStartDay;
                            const endMonthDay = closureEndMonth * 100 + closureEndDay;

                            if (closureStartMonth <= closureEndMonth) {
                              isInRange = checkMonthDay >= startMonthDay && checkMonthDay <= endMonthDay;
                            } else {
                              isInRange = checkMonthDay >= startMonthDay || checkMonthDay <= endMonthDay;
                            }
                          } else {
                            isInRange = dateString >= closureStartYmd && dateString <= closureEndYmd;
                          }

                          if (isInRange && (closure as any).closureType === 'all_day') return closure;
                        }

                        return null;
                      };
                      
                      // Helper function to check if date is disabled
                      const isDateDisabled = (date: Date): boolean => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date < today) return true;
                        
                        // CHECK BUSINESS CLOSURES FIRST - before any other availability logic
                        if (getAllDayClosureForDate(date)) {
                          return true; // Disable date due to all-day closure
                        }
                        
                        // Check if any selected professional has an all-day time off on this date
                        if (hasAllDayTimeOff(date, professionalIds)) {
                          return true;
                        }
                        
                        // Business operating hours must be enforced even when a professional is selected.
                        const dayOfWeek = date.getDay();
                        const dayOperatingHours = operatingHours.find((h: any) => {
                          const dow = (h?.dayOfWeek ?? h?.day_of_week);
                          return Number(dow) === dayOfWeek;
                        });
                        const isBusinessClosedForDay = dayOperatingHours
                          ? Boolean((dayOperatingHours as any).isClosed ?? (dayOperatingHours as any).is_closed)
                          : true;
                        if (!dayOperatingHours || isBusinessClosedForDay) return true;
                        
                        // If professionals are selected, check their availability
                        if (professionalIds.length > 0) {
                          // First check if professional has any schedule at all
                          if (!hasProfessionalSchedule()) {
                            return true; // Professional has no schedule, disable all dates
                          }
                          
                          // Check if professional works on this day of week based on their schedule
                          if (!professionalWorksOnDay(dayOfWeek)) {
                            return true; // Professional doesn't work on this day
                          }
                          
                          const dateString = formatLocalYmd(date);
                          const availability = getAvailabilityForDate(dateString, professionalIds);
                          
                          // If we have availability data, check if professionals are available
                          // But only disable if we're certain - if professional has schedule for this day,
                          // trust the schedule over availability data (availability might be incomplete)
                          if (availability) {
                            // Only disable if we have explicit availability data AND no professionals AND no time slots
                            // This means the backend has checked and confirmed no availability
                            if (availability.availableProfessionals && 
                                availability.availableProfessionals.length === 0 &&
                                availability.timeSlots && 
                                availability.timeSlots.length === 0) {
                              // Backend confirmed no availability - but still check if professional has schedule
                              // If they have schedule for this day, enable it optimistically (might be a data issue)
                              if (professionalWorksOnDay(dayOfWeek)) {
                                return false; // Trust schedule over availability data
                              }
                              return true; // No professionals available and no schedule
                            }
                            // If we have time slots or available professionals, date is available
                            if (availability.timeSlots && availability.timeSlots.length > 0) {
                              return false; // Has time slots, date is available
                            }
                            if (availability.availableProfessionals && availability.availableProfessionals.length > 0) {
                              return false; // Has available professionals, date is available
                            }
                            // If availability data exists but is incomplete, trust schedule
                            if (professionalWorksOnDay(dayOfWeek)) {
                              return false; // Trust schedule
                            }
                          }
                          
                          // If no availability data yet but professional has schedule and works on this day,
                          // enable the date optimistically (availability will be checked when data loads)
                          return false; // Enable date based on schedule
                        } else {
                          // No professionals selected: operating hours already enforced above.
                        }
                        
                        return false;
                      };
                      
                      const isDateDisabledDueToUnavailability = (date: Date): boolean => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date < today) return false;
                        
                        // NOTE: business closures are handled by `isDateDisabled` (and styled/tooltipped separately).
                        // This helper is specifically about unavailability (operating hours / schedules / availability).
                        const dateString = formatLocalYmd(date);
                        
                        if (professionalIds.length === 0) {
                          const dayOfWeek = date.getDay();
                          const dayOperatingHours = operatingHours.find((h: any) => {
                            const dow = (h?.dayOfWeek ?? h?.day_of_week);
                            return Number(dow) === dayOfWeek;
                          });
                          const isClosed = dayOperatingHours
                            ? Boolean((dayOperatingHours as any).isClosed ?? (dayOperatingHours as any).is_closed)
                            : true;
                          return !dayOperatingHours || isClosed;
                        }
                        
                        // Check if professional has no schedule at all
                        if (!hasProfessionalSchedule()) {
                          return true; // Professional has no schedule
                        }

                        const dayOfWeek = date.getDay();
                        if (!professionalWorksOnDay(dayOfWeek)) {
                          return true;
                        }
                        
                        const availability = getAvailabilityForDate(dateString, professionalIds);
                        if (availability && availability.availableProfessionals) {
                          // Only return true if we have complete availability data showing no professionals
                          // AND no time slots (meaning backend confirmed no availability)
                          if (availability.availableProfessionals.length === 0) {
                            // Check if we also have no time slots - if so, it's confirmed unavailable
                            if (availability.timeSlots && availability.timeSlots.length === 0) {
                              // But still check if professional has schedule - if they do, trust schedule
                              const dayOfWeek = date.getDay();
                              if (professionalWorksOnDay(dayOfWeek)) {
                                return false; // Trust schedule over availability data
                              }
                              return true; // Confirmed unavailable
                            }
                            // If we have time slots but no available professionals, might be incomplete data
                            // Trust schedule if professional works on this day
                            const dayOfWeek = date.getDay();
                            if (professionalWorksOnDay(dayOfWeek)) {
                              return false; // Trust schedule
                            }
                          }
                          return false; // Has available professionals
                        }
                        // If no availability data yet, consider it unavailable (waiting for data)
                        return true;
                      };
                      
                      return days.map((date, index) => {
                        if (!date) {
                          return (
                            <div key={index} className="p-1">
                              <div className="w-full h-10"></div>
                            </div>
                          );
                        }

                        const allDayClosure = getAllDayClosureForDate(date);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        // Past dates stay greyed (with hatch) but remain selectable for business back-dating.
                        const isPastDate = date < today;
                        // Business booking flow: keep indicators for closures/unavailability, but do NOT block selection.
                        const isUnavailableDate = !isPastDate && isDateDisabled(date);
                        const isSelectedDate =
                          !!bookingData.selectedDate &&
                          date.toDateString() === bookingData.selectedDate.toDateString();
                        const closureTooltip =
                          allDayClosure &&
                          ('Closed: ' + (String((allDayClosure as any).reason || (allDayClosure as any).description || '').trim() ||
                            'Business is closed on this day'));

                        const getUnavailableDateStyle = (): React.CSSProperties => {
                          if (allDayClosure) {
                            return { color: '#BE185D', backgroundColor: '#FCE7F3', border: '1px solid #F9A8D4' };
                          }
                          if (hasAllDayTimeOff(date, professionalIds)) {
                            return { color: '#9CA3AF', backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' };
                          }
                          const dayOfWeek = date.getDay();
                          const dayOperatingHours = operatingHours.find(h => h.dayOfWeek === dayOfWeek);
                          const businessClosed =
                            operatingHours.length === 0 || !dayOperatingHours || dayOperatingHours.isClosed;
                          if (professionalIds.length > 0 && !professionalWorksOnDay(dayOfWeek) && !businessClosed) {
                            return { color: '#EF4444', backgroundColor: '#FEF2F2', border: '1px solid #FECACA' };
                          }
                          if (isDateDisabledDueToUnavailability(date)) {
                            if (operatingHours.length > 0) {
                              if (dayOperatingHours && dayOperatingHours.isClosed) {
                                return { color: '#EA580C', backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' };
                              }
                              if (!dayOperatingHours) {
                                return { color: '#EA580C', backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' };
                              }
                            }
                            if (operatingHours.length === 0) {
                              return { color: '#EA580C', backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' };
                            }
                            return { color: '#EF4444', backgroundColor: '#FEF2F2', border: '1px solid #FECACA' };
                          }
                          return { color: '#9CA3AF', backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' };
                        };

                        return (
                          <div key={index} className="p-1">
                            <button
                              type="button"
                              onClick={() => {
                                handleDateChange(date);
                                if (bookingData.selectedTime) {
                                  handleTimeChange('');
                                }
                              }}
                              className={`w-full h-10 rounded-md text-sm font-medium transition-all ${
                                isSelectedDate
                                  ? 'shadow-sm'
                                  : isPastDate
                                  ? 'opacity-80 hover:opacity-100'
                                  : isUnavailableDate
                                  ? 'opacity-90'
                                  : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'
                              }`}
                              style={
                                isPastDate && !isSelectedDate
                                  ? PAST_SELECTABLE_SLOT_STYLE
                                  : isUnavailableDate
                                  ? isSelectedDate
                                    ? { ...getUnavailableDateStyle(), border: '2px solid #3B82F6' }
                                    : getUnavailableDateStyle()
                                  : isSelectedDate
                                  ? {
                                      color: '#FFFFFF',
                                      backgroundColor: '#3B82F6',
                                      border: '1px solid #3B82F6'
                                    }
                                  : { color: '#374151', backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }
                              }
                              title={
                                isPastDate || isUnavailableDate
                                  ? (() => {
                                      if (isPastDate) {
                                        return 'Past date (override allowed)';
                                      }

                                      if (closureTooltip) return closureTooltip;
                                      
                                      // Check if any selected professional has an all-day time off on this date
                                      if (hasAllDayTimeOff(date, professionalIds)) {
                                        return 'The professional is not available on this date.';
                                      }
                                      
                                      // Check if it's disabled due to unavailability
                                      if (isDateDisabledDueToUnavailability(date)) {
                                        const dayOfWeek = date.getDay();
                                        const dayOperatingHours = operatingHours.find(h => h.dayOfWeek === dayOfWeek);
                                        if (operatingHours.length > 0) {
                                          if (dayOperatingHours && dayOperatingHours.isClosed) {
                                            return 'Business is closed on this day';
                                          }
                                          if (!dayOperatingHours) {
                                            return 'No operating hours set for this day';
                                          }
                                        }
                                        if (operatingHours.length === 0) {
                                          return 'No operating hours set for the business';
                                        }
                                        return 'No professionals available on this date';
                                      }
                                      
                                      return 'Unavailable';
                                    })()
                                  : ''
                              }
                            >
                              {date.getDate()}
                            </button>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Time Selection */}
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                  <div className="mb-6">
                    <h3 className="text-lg text-gray-900">Select Time</h3>
                  </div>
                  
                  {!bookingData.selectedDate ? (
                    <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                      <p className="text-sm text-gray-600 text-center">Please select a date first</p>
                    </div>
                  ) : (() => {
                    const professionalIds = getSelectedProfessionalIds();
                    const dateString = formatLocalYmd(bookingData.selectedDate);
                    const availability = getAvailabilityForDate(dateString, professionalIds);
                    
                    // Get time slots for the selected date - EXACTLY like BookingStep3.tsx
                    // If we have availability data, use ALL time slots from there (including conflicting ones)
                    // The isTimeSlotAvailable function will disable slots without available professionals
                    // Otherwise, generate from schedule/operating hours
                    let timeSlots: string[] = [];
                    let timeSlotsFromAvailability = false;
                    
                    // Check if availability data exists but has empty timeSlots
                    // This could mean all slots are conflicted, so we should not generate from schedule
                    const hasAvailabilityDataButNoSlots = availability && 
                      availability.timeSlots && 
                      Array.isArray(availability.timeSlots) &&
                      availability.timeSlots.length === 0;
                    
                    if (availability && availability.timeSlots && availability.timeSlots.length > 0) {
                      // Return all time slots (not filtered) - conflicting ones will be disabled by isTimeSlotAvailable
                      // Deduplicate time slots in case backend returns duplicates
                      let allTimeSlots = availability.timeSlots.map((slot: any) => slot.time);
                      allTimeSlots = Array.from(new Set(allTimeSlots)).sort() as string[];
                      
                      if (allTimeSlots.length > 0) {
                        allTimeSlots = extendAvailabilityTimeSlots(
                          allTimeSlots,
                          slotStepMinutes,
                          getOriginalBusinessHoursRangeForDate(bookingData.selectedDate, professionalIds),
                          { fillCoreGaps: true }
                        );
                      }
                      
                      timeSlots = allTimeSlots;
                      timeSlotsFromAvailability = true;
                    } else if (hasAvailabilityDataButNoSlots) {
                      // Availability data exists but has no time slots
                      // This could mean:
                      // 1. All slots are conflicted (backend should still return them, but marked as unavailable)
                      // 2. Backend issue - not returning slots properly
                      // For now, we'll still generate from schedule but mark them all as unavailable
                      // The isTimeSlotAvailable function will check availability data
                      // But if backend returned 0 slots, we need to generate from schedule to show them
                      // and let the conflict check happen
                      timeSlots = [];
                      timeSlotsFromAvailability = false;
                    } else if (loadingAvailability && professionalIds.length > 0) {
                      // If no availability data yet, but we're loading, return empty array to avoid showing incorrect slots
                      return (
                        <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                              </div>
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-yellow-900 mb-1">Loading Availability</h4>
                              <p className="text-sm text-yellow-800">
                                Loading available time slots for the selected professional...
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    } else if (professionalIds.length > 0) {
                      // Generate from professional schedule (fallback)
                      const selectedProfessionalId = professionalIds[0];
                      const schedule = professionalSchedules[selectedProfessionalId];
                      
                      // Check if schedules are still loading
                      if (loadingSchedules) {
                        return (
                          <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                </div>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-yellow-900 mb-1">Loading Schedule</h4>
                                <p className="text-sm text-yellow-800">
                                  Loading professional schedule...
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      
                      if (!schedule || schedule.length === 0) {
                        return (
                          <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                </div>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-yellow-900 mb-1">No Work Schedule</h4>
                                <p className="text-sm text-yellow-800">
                                  The selected professional has no work schedule configured. Please select a different professional or configure their schedule first.
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      
                      const range = getOriginalBusinessHoursRangeForDate(bookingData.selectedDate, professionalIds);
                      if (range) {
                        const extendedStart = Math.max(0, range.startMinutes - 180);
                        const extendedEnd = Math.min(24 * 60 - 1, range.endMinutes + 180);
                        for (let minutes = extendedStart; minutes < extendedEnd; minutes += slotStepMinutes) {
                          const timeStr = minutesToHhmm(minutes);
                          if (timeStr && !timeSlots.includes(timeStr)) {
                            timeSlots.push(timeStr);
                          }
                        }
                      }
                    } else {
                      // No professionals selected, use operating hours
                      const dayOfWeek = bookingData.selectedDate.getDay();
                      const dayOperatingHours = operatingHours.find(h => h.dayOfWeek === dayOfWeek);
                      
                      if (!dayOperatingHours || dayOperatingHours.isClosed) {
                        return (
                          <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                </div>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-yellow-900 mb-1">No Available Times</h4>
                                <p className="text-sm text-yellow-800">
                                  {!dayOperatingHours 
                                    ? `No operating hours set for this date.`
                                    : `The business is closed on this date.`}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      
                      const hoursRange = getOriginalBusinessHoursRangeForDate(bookingData.selectedDate, []);
                      if (hoursRange) {
                        const extendedStart = Math.max(0, hoursRange.startMinutes - 180);
                        const extendedEnd = Math.min(24 * 60 - 1, hoursRange.endMinutes + 180);
                        for (let minutes = extendedStart; minutes < extendedEnd; minutes += slotStepMinutes) {
                          const timeStr = minutesToHhmm(minutes);
                          if (timeStr) timeSlots.push(timeStr);
                        }
                      }
                    }
                    
                    // If no time slots available
                    if (timeSlots.length === 0) {
                      return (
                        <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                              </div>
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-yellow-900 mb-1">No Available Times</h4>
                              <p className="text-sm text-yellow-800">
                                No available time slots for this date. Please select a different date.
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Deduplicate and sort time slots before rendering
                    timeSlots = buildHourDisplayTimeSlots(
                      Array.from(new Set(timeSlots)).sort(),
                      bookingSlotMinutes === 60
                    );
                    
                    const getOriginalBusinessHoursRange = (): { startMinutes: number, endMinutes: number } | null =>
                      getOriginalBusinessHoursRangeForDate(bookingData.selectedDate, professionalIds);
                    
                    // Helper function to check if a time slot is in the extended range (3 hours before/after)
                    const isExtendedHour = (timeStr: string): boolean => {
                      const originalRange = getOriginalBusinessHoursRange();
                      if (!originalRange) return false;
                      
                      // Parse time string to minutes
                      const parseTimeToMinutes = (time: string): number => {
                        // Remove AM/PM if present
                        const [timePart, period] = time.split(' ');
                        const [hours, minutes] = timePart.split(':').map(Number);
                        let hour24 = hours;
                        if (period) {
                          if (period.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
                          if (period.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;
                        }
                        return hour24 * 60 + minutes;
                      };
                      
                      const timeMinutes = parseTimeToMinutes(timeStr);
                      const { startMinutes, endMinutes } = originalRange;
                      
                      // Check if time is outside original range but within extended range (3 hours = 180 minutes)
                      return timeMinutes < startMinutes || timeMinutes >= endMinutes;
                    };
                    
                    // Format time for display (convert from 24h to 12h if needed) - EXACTLY like BookingStep3.tsx
                    const formatTimeDisplay = (timeStr: string): string => {
                      // If already in 12h format, return as is
                      if (timeStr.includes('AM') || timeStr.includes('PM')) {
                        return timeStr;
                      }
                      // Convert from 24h format (HH:MM) to 12h format
                      const [hours, minutes] = timeStr.split(':').map(Number);
                      const period = hours >= 12 ? 'PM' : 'AM';
                      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
                      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
                    };
                    
                    // Check if today - EXACTLY like BookingStep3.tsx
                    const today = new Date();
                    const selectedDateOnly = new Date(bookingData.selectedDate.getFullYear(), bookingData.selectedDate.getMonth(), bookingData.selectedDate.getDate());
                    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    const isToday = selectedDateOnly.getTime() === todayOnly.getTime();
                    
                    // Check if time slot is available (extended hours are always available) - EXACTLY like BookingStep3.tsx
                    const isTimeSlotAvailable = (time: string) => {
                      // Extended hours are always available
                      if (isExtendedHour(time)) return true;
                      if (!bookingData.selectedDate) return true;
                      
                      // CHECK BUSINESS CLOSURES FIRST - before any other availability logic
                      const dateString = formatLocalYmd(bookingData.selectedDate);
                      const checkMonth = bookingData.selectedDate.getMonth() + 1; // getMonth() returns 0-11
                      const checkDay = bookingData.selectedDate.getDate();

                      // IMPORTANT: business closures are DATE-only concepts. Do NOT parse them with `new Date("YYYY-MM-DD")`,
                      // because JS treats that as UTC midnight which can shift to the previous day in local timezones.
                      const toYmd = (v: any): string | null => {
                        if (!v) return null;
                        if (typeof v === 'string') {
                          const s = v.trim();
                          if (!s) return null;
                          return s.includes('T') ? s.split('T')[0] : s;
                        }
                        if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10);
                        return null;
                      };
                      
                      // Check if this time slot falls within any time-range closure
                      const [hours, minutes] = time.split(':').map(Number);
                      const timeSlotMinutes = hours * 60 + minutes;
                      const slotEndMinutes = timeSlotMinutes + bookingData.totalDuration;
                      
                      const hasClosureConflict = businessClosures.some(closure => {
                        const closureStartYmd = toYmd(closure.startDate);
                        const closureEndYmd = toYmd(closure.endDate);
                        if (!closureStartYmd || !closureEndYmd) return false;
                        
                        let isInDateRange = false;
                        
                        // Check if closure repeats yearly
                        if (closure.repeatYearly) {
                          // For yearly repeating closures, check if the month and day match
                          const startParts = closureStartYmd.split('-').map(Number);
                          const endParts = closureEndYmd.split('-').map(Number);
                          if (startParts.length < 3 || endParts.length < 3) return false;
                          const closureStartMonth = startParts[1];
                          const closureStartDay = startParts[2];
                          const closureEndMonth = endParts[1];
                          const closureEndDay = endParts[2];
                          
                          // Check if current date's month/day falls within the closure's month/day range
                          // Handle cases where closure spans across year boundary (e.g., Dec 20 - Jan 5)
                          if (closureStartMonth <= closureEndMonth) {
                            // Normal case: closure within same year (e.g., Jan 1 - Jan 7)
                            const checkMonthDay = checkMonth * 100 + checkDay; // e.g., 101 for Jan 1
                            const startMonthDay = closureStartMonth * 100 + closureStartDay;
                            const endMonthDay = closureEndMonth * 100 + closureEndDay;
                            isInDateRange = checkMonthDay >= startMonthDay && checkMonthDay <= endMonthDay;
                          } else {
                            // Year boundary case: closure spans across year (e.g., Dec 20 - Jan 5)
                            const checkMonthDay = checkMonth * 100 + checkDay;
                            const startMonthDay = closureStartMonth * 100 + closureStartDay;
                            const endMonthDay = closureEndMonth * 100 + closureEndDay;
                            isInDateRange = checkMonthDay >= startMonthDay || checkMonthDay <= endMonthDay;
                          }
                        } else {
                          // Non-repeating closure: compare by YYYY-MM-DD (date-only).
                          isInDateRange = dateString >= closureStartYmd && dateString <= closureEndYmd;
                        }
                        
                        if (!isInDateRange || closure.closureType !== 'time_range') {
                          return false;
                        }
                        
                        // Check if time slot overlaps with closure time range
                        if (!closure.startTime || !closure.endTime) {
                          return false;
                        }
                        
                        // Parse closure times (format: HH:mm:ss or HH:mm)
                        const closureStartParts = closure.startTime.split(':').map(Number);
                        const closureEndParts = closure.endTime.split(':').map(Number);
                        const closureStartMinutes = closureStartParts[0] * 60 + closureStartParts[1];
                        const closureEndMinutes = closureEndParts[0] * 60 + closureEndParts[1];
                        
                        // Check if slot overlaps with closure (slotStart < closureEnd && slotEnd > closureStart)
                        return timeSlotMinutes < closureEndMinutes && slotEndMinutes > closureStartMinutes;
                      });
                      
                      if (hasClosureConflict) {
                        return false; // Time slot is within a closure time range
                      }
                      
                      // Check if the time has already passed for today
                      if (isToday) {
                        const timeSlot = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
                        const now = new Date();
                        const bufferTime = 15 * 60 * 1000; // 15 minutes
                        if (timeSlot.getTime() <= now.getTime() + bufferTime) {
                          return false;
                        }
                      }
                      
                      // If no professionals selected, all time slots are available
                      if (professionalIds.length === 0) return true;
                      
                      // Always check availability data if it exists, regardless of where time slots came from
                      // This ensures conflicts are detected even when time slots were generated from schedule
                      if (availability && availability.timeSlots && availability.timeSlots.length > 0) {
                        // Normalize time format for comparison (both should be HH:mm, but handle edge cases)
                        const normalizeTime = (t: string) => {
                          // Remove AM/PM if present and convert to 24h
                          if (t.includes('AM') || t.includes('PM')) {
                            const [timePart, period] = t.split(' ');
                            const [hours, minutes] = timePart.split(':').map(Number);
                            let hour24 = hours;
                            if (period?.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
                            if (period?.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;
                            return `${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                          }
                          return t; // Already in 24h format
                        };
                        
                        const normalizedTime = normalizeTime(time);
                        
                        // Check if the time slot exists in availability data
                        const timeSlot = availability.timeSlots.find((slot: any) => {
                          const slotTime = normalizeTime(slot.time);
                          return slotTime === normalizedTime;
                        });
                        
                        if (timeSlot) {
                          return isOwnerDisplaySlotAvailable(timeSlot, professionalIds);
                        }
                        
                        // Missing from the API is not a conflict. The backend omits starts that
                        // cannot fit the selected duration before close; it still returns
                        // appointment overlaps with empty availableProfessionals.
                        return true;
                      }
                      
                      // If no availability data exists yet, trust the schedule
                      // Conflicts will be checked on submit
                      return true;
                    };
                    
                    // Check if time slot has passed - EXACTLY like BookingStep3.tsx
                    const isTimeSlotPassed = (time: string) => {
                      if (!isToday) return false;
                      const [hours, minutes] = time.split(':').map(Number);
                      const timeSlot = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
                      const now = new Date();
                      const bufferTime = 15 * 60 * 1000;
                      return timeSlot.getTime() <= now.getTime() + bufferTime;
                    };
                    
                    return (
                      <>
                        <div className="grid grid-cols-3 gap-2">
                          {timeSlots.map((time: string) => {
                            const isExtended = isExtendedHour(time);
                            const isAvailable = isExtended ? true : isTimeSlotAvailable(time); // Extended hours are always available
                            const isPassed = isTimeSlotPassed(time);
                            const isDurationLeadIn = isDurationLeadInSlot(getAvailabilitySlotForTime(availability, time));
                            const formattedTime = formatTimeDisplay(time);
                            const isSelected = bookingData.selectedTime === formattedTime || bookingData.selectedTime === time;
                            
                            return (
                              <button
                                key={time}
                                type="button"
                                onClick={() => handleTimeChange(formattedTime)}
                                className={`relative p-3 rounded-md text-sm font-medium transition-all ${
                                  isSelected
                                    ? 'shadow-sm'
                                    : isPassed
                                    ? 'opacity-80 hover:opacity-100'
                                    : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'
                                } ${isExtended ? 'italic' : ''}`}
                                style={
                                  isSelected
                                    ? {
                                        color: '#FFFFFF',
                                        backgroundColor: '#3B82F6',
                                        border: '1px solid #3B82F6',
                                        ...(isExtended ? { fontStyle: 'italic' as const } : {})
                                      }
                                  : isPassed
                                    ? PAST_SELECTABLE_SLOT_STYLE
                                  : !isAvailable
                                    ? { color: '#991B1B', backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }
                                  : isDurationLeadIn
                                    ? DURATION_LEAD_IN_SLOT_STYLE
                                  : isExtended
                                    ? {
                                        color: '#374151',
                                        backgroundColor: '#F7FEF9', // Even lighter green
                                        border: '1px solid #D1FAE5',
                                        fontStyle: 'italic'
                                      }
                                  : { color: '#374151', backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }
                                }
                                title={
                                  isDurationLeadIn && !isPassed && isAvailable
                                    ? DURATION_LEAD_IN_TOOLTIP
                                    : isPassed
                                    ? `Past time (override allowed): ${formattedTime}`
                                    : isExtended
                                    ? `Extended hours - ${formattedTime} (outside normal business hours)`
                                    : !isAvailable
                                    ? `Unavailable slot (override allowed): ${formattedTime}`
                                    : `Select ${formattedTime}`
                                }
                              >
                                {formattedTime}
                                {isDurationLeadIn && !isPassed && isAvailable && !isSelected && (
                                  <span
                                    className="absolute top-1 right-1 inline-flex items-center justify-center w-5 h-5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                                    title={DURATION_LEAD_IN_TOOLTIP}
                                    aria-label={DURATION_LEAD_IN_TOOLTIP}
                                  >
                                    <AlertTriangle className="w-3 h-3" />
                                  </span>
                                )}
                                {!isAvailable && !isPassed && !isExtended && (
                                  <span
                                    className="absolute top-1 right-1 inline-flex items-center justify-center w-5 h-5 rounded bg-red-100 text-red-700 border border-red-200"
                                    title="Unavailable slot"
                                    aria-label="Unavailable slot"
                                  >
                                    <AlertTriangle className="w-3 h-3" />
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
                )}
            </div>
          )}

          {/* Recurring (Optional) - normal (non-prefilled) date/time flow */}
          {hasAtLeastOneNonAddOnServiceSelected && !hasPrefilledDateTime && (
            <div className="mt-6">
              {renderRecurringSection()}
            </div>
          )}

          {/* Service Address (when at least one ON_SITE service is selected) */}
          {hasOnSiteService && (
            <div className="mt-6">
              <BookingStep4ServiceAddress
                serviceAddress={bookingData.serviceAddress || { address: '', city: '', state: '', zip: '', locationNotes: '', locationLabel: '' }}
                onServiceAddressChange={(next) => updateBookingData({ serviceAddress: next })}
                rememberServiceAddress={bookingData.rememberServiceAddress === true}
                onRememberServiceAddressChange={(next) => updateBookingData({ rememberServiceAddress: next })}
                preferredServiceAddress={undefined}
                themeStyles={{ inlineStyles: { buttonPrimary: { backgroundColor: '#3B82F6' } } }}
                showRememberOption={false}
              />
            </div>
          )}

          {/* Private Professional Notes */}
          {hasAtLeastOneNonAddOnServiceSelected && bookingData.selectedDate && bookingData.selectedTime && (
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#3B82F620' }}
                  >
                    <Lock className="w-4 h-4" style={{ color: '#3B82F6' }} />
                  </div>
                  <h2 className="text-xl text-gray-900">Private Professional Notes</h2>
                </div>
                <p className="text-sm text-gray-600 mb-4">Only visible to you and authorized staff.</p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                <textarea
                  value={privateNotes}
                  onChange={(e) => setPrivateNotes(e.target.value)}
                  placeholder="Enter private notes about this appointment..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={4}
                />
              </div>
            </div>
          )}

          {/* Summary & Submit */}
          {hasAtLeastOneNonAddOnServiceSelected && bookingData.selectedDate && bookingData.selectedTime && (
            <div className="border-t border-gray-200 pt-6">
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#3B82F620' }}
                  >
                    <FileText className="w-4 h-4" style={{ color: '#3B82F6' }} />
                  </div>
                  <h3 className="text-xl text-gray-900">Appointment Summary</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Customer:</span>
                    <span className="font-medium text-gray-900">
                      {selectedCustomer 
                        ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`
                        : manualCustomer.email
                        ? `${manualCustomer.firstName} ${manualCustomer.lastName} (${manualCustomer.email})`
                        : 'Not selected'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Services:</span>
                    <div className="font-medium text-gray-900 text-right">
                      {bookingData.selectedServices.map((s, index) => (
                        <div key={s.id || index}>{s.name}</div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date & Time:</span>
                    <span className="font-medium text-gray-900">
                      {bookingData.selectedDate?.toLocaleDateString()} at {bookingData.selectedTime}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Duration:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {Math.floor(bookingData.totalDuration / 60)}h {bookingData.totalDuration % 60}m
                      </span>
                    </div>
                  </div>
                  {(() => {
                    const professionalName = getAutoSelectedProfessionalName();
                    if (professionalName) {
                      return (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Professional:</span>
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                            <UserIcon className="w-3.5 h-3.5 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">
                              {professionalName}
                            </span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <div className="flex justify-between text-lg pt-3 border-t border-gray-200">
                    <span className="font-semibold text-gray-900">Total Price:</span>
                    <span className="font-bold text-blue-600">
                      ${bookingData.totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={submitting || !isFormValid() || timeConflict}
                className="w-full aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating Appointment...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Create Appointment
                  </>
                )}
              </button>
            </div>
          )}
        </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OwnerBookingPage;

